<script setup>
import { computed, inject, nextTick, ref, watch, onUnmounted } from 'vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import FiscalPreviewCard from '@/components/base/fiscal/FiscalPreviewCard.vue'
import api from '@/services/api'
import { PROD_NATUREZA } from '@/constants/PROD_NATUREZA'
import { GEO_PAISES } from '@/constants/GEO_PAISES'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { SUP_ST_DOC_ENT } from '@/constants/SUP_ST_DOC_ENT'
import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { useNavegacao } from '@/composables/useNavegacao'
import { useConversaoQuantidade } from '@/composables/useConversaoQuantidade'

const props = defineProps({
  itensDocumento: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  accordionFechado: { type: Boolean, default: false },
  idDocumentoEntrada: { type: Number, default: null },
  habilitarParceiroProdutor: { type: Boolean, default: false },
  configBuscaLoteFabricacao: { type: Object, default: () => ({}) },
  lstMoedaDocumento: { type: String, default: '' },
  idParceiroNegocioFornecedor: { type: Number, default: null },
  nivelFiscal: { type: String, default: NV_MODULO_FISCAL.Nenhum },
  modoAlocacao: { type: String, default: 'livre' },
  contextoOrigem: { type: String, default: 'outros' },
  lstStatusDocumento: { type: String, default: '' },
  fiscalPreviewParams: { type: Object, default: null },
  /// <summary>Valor padrão para IdLocalEstoque, propagado do cabeçalho do DocumentoEntrada.
  /// Quando preenchido, é incluído no payload de alocação/atualização de itens
  /// para que o motor de estoque use este local como destino do movimento.</summary>
  idLocalEstoquePadrao: { type: Number, default: null },
  /// <summary>Indica se a operação do documento movimenta estoque físico.
  /// Quando true, habilita o campo de Local de Estoque no modal de alocação.</summary>
  movimentaEstoque: { type: Boolean, default: false },
  /// <summary>Configuração de busca para o campo Local de Estoque.</summary>
  configBuscaLocalEstoque: { type: Object, default: null },
  /// <summary>Quando true, exibe o campo "Modalidade Rateio" no modal do item.
  /// Oculta por default. Ativado via prop quando a operação não é nacional (ex: importação).</summary>
  exibirModalidadeRateio: { type: Boolean, default: false },
  /// <summary>Quando true, indica que a operação é nacional (Ordem de Compra).
  /// Usado para auto-preenche o País de Origem com Brasil (GEO_PAISES26).</summary>
  ehOperacaoNacional: { type: Boolean, default: false }
})

// GUARDIÃO: Detecta natureza do item (Produto Físico vs Serviço)
const getNaturezaItem = (item) => {
  const natureza = item.lstNaturezaItem || ''
  if (natureza === PROD_NATUREZA.Servico) return 'servico'
  return 'produto' // default: ProdutoFisico
}

const emit = defineEmits([
  'alocar-item',
  'atualizar-item',
  'remover-item',
  'show-notification'
])

// Injeta o registro de erros de validação fornecido pelo DocumentoEntradaModal
// (mesmo formErrors usado pelos componentes CampoBusca/CampoBuscaLista via inject).
const formErrors = inject('formErrors', ref({}))
/// <summary>Erro de validação do campo Local de Estoque.
/// Usa :error em vez de formErrors porque o CampoBusca tem o guard hasValidDescription
/// que suprime formErrors quando descricao.value está preenchido.</summary>
const localEstoqueError = ref('')

const mostrarSaldos = ref(false)
const docAtualId = ref(null)
const confirmationModalRef = ref(null)
const toleranceModalRef = ref(null)
const toleranceWarningMessage = ref('')
const produtoFornecedorModalRef = ref(null)
const editingItemId = ref(null)
const isSettingFractionData = ref(false)
const isVisualizacaoFiscal = ref(false)
/// <summary>Armazena o último país de origem selecionado (exceto auto-preenchimento Brasil)
/// para exibir como sugestão fantasma no campo País de Origem.</summary>
const ultimoPaisOrigem = ref(null)

/// <summary>Saldos disponíveis carregados autonomamente do backend via
/// GET /DocumentosEntrada/{id}/saldos-disponiveis.
/// Substitui a antiga prop produtosComSaldo que era calculada pelos componentes pai,
/// eliminando o "telefone sem fio" entre PO/OC → Grid → Modal → SmartMode.</summary>
const saldosDisponiveis = ref([])
const saldosLoading = ref(false)

const carregarSaldosDisponiveis = async () => {
  const idDoc = props.idDocumentoEntrada
  if (!idDoc) {
    saldosDisponiveis.value = []
    return
  }
  saldosLoading.value = true
  try {
    const response = await api.get(`/DocumentosEntrada/${idDoc}/saldos-disponiveis`, { _suppressLoading: true })
    saldosDisponiveis.value = response.data || []
    // Se há saldos e o documento está vazio, abre o painel automaticamente
    if (saldosDisponiveis.value.length > 0 && props.itensDocumento.length === 0) {
      mostrarSaldos.value = true
    }
  } catch (e) {
    console.warn('[SmartMode] Erro ao carregar saldos disponíveis:', e)
    saldosDisponiveis.value = []
  } finally {
    saldosLoading.value = false
  }
}

const temFiscalPersistido = computed(() => {
  const d = fractionModalData.value
  return d.valorIcmsCalculado != null ||
         d.valorIcmsEfetivoCalculado != null ||
         d.valorPisCalculado != null ||
         d.valorCofinsCalculado != null ||
         d.valorDifalCalculado != null ||
         d.valorIpiCalculado != null ||
         d.valorIeCalculado != null ||
         d.valorIiCalculado != null ||
         d.valorIssRetido != null ||
         d.valorIrrfRetido != null ||
         d.valorInssRetido != null ||
         d.valorPisRetido != null ||
         d.valorCofinsRetido != null ||
         d.valorCsllRetida != null ||
         d.fatorImpostosAplicado != null
})

const totalImpostosItem = computed(() => {
  const d = fractionModalData.value
  // Usa apenas ICMS Efetivo (já inclui o nominal quando não há incentivo) — NÃO duplica
  return (Number(d.valorIcmsEfetivoCalculado) || 0) +
         (Number(d.valorPisCalculado) || 0) +
         (Number(d.valorCofinsCalculado) || 0) +
         (Number(d.valorDifalCalculado) || 0) +
         (Number(d.valorIeCalculado) || 0) +
         (Number(d.valorIiCalculado) || 0) +
         (Number(d.valorIpiCalculado) || 0) +
         (Number(d.valorIssRetido) || 0) +
         (Number(d.valorIrrfRetido) || 0) +
         (Number(d.valorInssRetido) || 0) +
         (Number(d.valorPisRetido) || 0) +
         (Number(d.valorCofinsRetido) || 0) +
         (Number(d.valorCsllRetida) || 0)
})

// ============================================================
// DADOS FISCAIS UNIFICADOS PARA EXIBIÇÃO (modal de alocação)
// Unifica a exibição entre simulação e dados persistidos do item,
// garantindo o mesmo layout para ambas as origens.
// ============================================================
const fiscalItemDisplayData = computed(() => {
  // Resultado da simulação
  if (simulacaoResultado.value?.sucesso) {
    const s = simulacaoResultado.value
    // Usa apenas o ICMS Efetivo (já inclui o nominal quando não há incentivo)
    const valorIcmsExibicao = Number(s.valorIcmsEfetivo) || 0
    const totalImpostos =
      valorIcmsExibicao +
      (Number(s.valorPis) || 0) +
      (Number(s.valorCofins) || 0) +
      (Number(s.valorDifal) || 0) +
      (Number(s.valorIe) || 0) +
      (Number(s.valorIi) || 0) +
      (Number(s.valorIpi) || 0) +
      (Number(s.valorIss) || 0) +
      (Number(s.valorIrrf) || 0) +
      (Number(s.valorInss) || 0) +
      (Number(s.valorPisRetido) || 0) +
      (Number(s.valorCofinsRetida) || 0) +
      (Number(s.valorCsll) || 0)
    return {
      _origem: 'simulacao',
      totalImpostos,
      valorTotal: Number(s.valorTotalComImpostos) || 0,
      valorLiquido: Number(s.valorLiquidoAPagar) || 0,
      fatorImpostos: Number(s.fatorImpostosAplicado) || 0,
      impostos: [
        { chave: 'icms', label: 'ICMS (Efetivo)', valor: valorIcmsExibicao, aliquota: s.aliquotaIcmsEfetiva },
        { chave: 'pis', label: 'PIS', valor: s.valorPis, aliquota: s.aliquotaPis },
        { chave: 'cofins', label: 'COFINS', valor: s.valorCofins, aliquota: s.aliquotaCofins },
        { chave: 'difal', label: 'DIFAL', valor: s.valorDifal, aliquota: s.aliquotaDifal },
        { chave: 'ie', label: 'IE', valor: s.valorIe },
        { chave: 'ii', label: 'II', valor: s.valorIi },
        { chave: 'ipi', label: 'IPI', valor: s.valorIpi, aliquota: s.aliquotaIpi },
        { chave: 'iss', label: 'ISS Retido', valor: s.valorIss },
        { chave: 'irrf', label: 'IRRF', valor: s.valorIrrf },
        { chave: 'inss', label: 'INSS', valor: s.valorInss },
        { chave: 'pisRetido', label: 'PIS Retido', valor: s.valorPisRetido },
        { chave: 'cofinsRetido', label: 'COFINS Retida', valor: s.valorCofinsRetida },
        { chave: 'csll', label: 'CSLL Retida', valor: s.valorCsll }
      ]
    }
  }
  // Dados persistidos do item (documento aprovado)
  if (temFiscalPersistido.value) {
    const d = fractionModalData.value
    const totalImpostos = totalImpostosItem.value
    const valorTotalLinha = (Number(d.quantidadeForn) || 0) * (Number(d.precoUnitario) || 0)
    // Usa o ValorLiquidoAPagarSnapshot se existir, senão calcula como fallback
    const valorLiquidoSnapshot = Number(d.valorLiquidoAPagarSnapshot) || 0
    return {
      _origem: 'persistido',
      totalImpostos,
      valorTotal: valorTotalLinha,
      valorLiquido: valorLiquidoSnapshot > 0 ? valorLiquidoSnapshot : valorTotalLinha,
      fatorImpostos: Number(d.fatorImpostosAplicado) || 0,
      impostos: [
        { chave: 'icms', label: 'ICMS (Efetivo)', valor: d.valorIcmsEfetivoCalculado, aliquota: d.aliquotaIcmsEfetivaAplicada },
        { chave: 'pis', label: 'PIS', valor: d.valorPisCalculado, aliquota: d.aliquotaPisAplicada },
        { chave: 'cofins', label: 'COFINS', valor: d.valorCofinsCalculado, aliquota: d.aliquotaCofinsAplicada },
        { chave: 'difal', label: 'DIFAL', valor: d.valorDifalCalculado, aliquota: d.aliquotaDifalAplicada },
        { chave: 'ie', label: 'IE', valor: d.valorIeCalculado },
        { chave: 'ii', label: 'II', valor: d.valorIiCalculado },
        { chave: 'ipi', label: 'IPI', valor: d.valorIpiCalculado, aliquota: d.aliquotaIpiAplicada },
        { chave: 'iss', label: 'ISS Retido', valor: d.valorIssRetido },
        { chave: 'irrf', label: 'IRRF', valor: d.valorIrrfRetido },
        { chave: 'inss', label: 'INSS', valor: d.valorInssRetido },
        { chave: 'pisRetido', label: 'PIS Retido', valor: d.valorPisRetido },
        { chave: 'cofinsRetido', label: 'COFINS Retida', valor: d.valorCofinsRetido },
        { chave: 'csll', label: 'CSLL Retida', valor: d.valorCsllRetida }
      ]
    }
  }
  return null
})

// Impostos visíveis (filtrados) para renderização — evita misturar v-for com v-if
const fiscalItemImpostosVisiveis = computed(() => {
  const data = fiscalItemDisplayData.value
  if (!data) return []
  return data.impostos.filter(imp => imp.valor)
})

// ============================================================
// SIMULAÇÃO FISCAL (prévia dos impostos no modal de alocação)
// ============================================================
const simulacaoLoading = ref(false)
const simulacaoResultado = ref(null)
let simulacaoDebounceTimer = null

const executarSimulacaoFiscal = async () => {
  // Se o documento já foi aprovado, não chama simulação — usa dados persistidos
  if (props.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado) {
    simulacaoResultado.value = null
    return
  }
  const params = props.fiscalPreviewParams
  const data = fractionModalData.value
  if (!params || !params.idOperacao || !params.lstUfDestino) {
    simulacaoResultado.value = null
    return
  }
  const valorBase = (Number(data.quantidadeForn) || 0) * (Number(data.precoUnitario) || 0)
  if (valorBase <= 0) {
    simulacaoResultado.value = null
    return
  }
  const isServico = getNaturezaItem(data) === 'servico'
  simulacaoLoading.value = true
  try {
    const payload = {
      idOperacao: params.idOperacao,
      lstUfOrigem: params.lstUfOrigem || 'GEO_UF28',
      lstUfDestino: params.lstUfDestino,
      lstTipoMovimento: TP_MOVIMENTO.Entrada,
      isServico,
      codigoNcm: isServico ? null : (data.codigoNcm || null),
      codigoNbs: isServico ? (data.lstCodigoNbs || null) : null,
      codigoLc116: isServico ? (data.lstCodigoLc116 || null) : null,
      lstNaturezaItem: data.lstNaturezaItem || null,
      valorBase,
      tipoPessoaParceiro: params.tipoPessoaParceiro || null,
      regimeTributarioParceiro: params.regimeTributarioParceiro || null,
      isOrgaoGovernoParceiro: !!params.isOrgaoGovernoParceiro,
      codigoIbge: params.codigoIbge || null,
      lstNivelModuloFiscalOverride: props.nivelFiscal !== NV_MODULO_FISCAL.Nenhum ? props.nivelFiscal : null
    }
    const res = await api.post('/Fiscal/simular', payload)
    simulacaoResultado.value = res.data
  } catch (e) {
    console.error('[SimulacaoFiscal] Erro ao simular impostos:', e)
    simulacaoResultado.value = null
  } finally {
    simulacaoLoading.value = false
  }
}

const agendarSimulacaoFiscal = () => {
  if (simulacaoDebounceTimer) clearTimeout(simulacaoDebounceTimer)
  simulacaoDebounceTimer = setTimeout(() => {
    executarSimulacaoFiscal()
  }, 600)
}

// Navega para a tela de cadastro Produto-Fornecedor em nova aba
const navegarParaProdutoFornecedor = () => {
  useNavegacao().abrirRegra('PRODUTOS_FORNECEDOR', 'adicionar')
}

// Handler do ConfirmationModal para ausência de ProdutoFornecedor
const handleProdutoFornecedorConfirm = (confirmed) => {
  if (confirmed) {
    navegarParaProdutoFornecedor()
  } else {
    emit('show-notification', {
      message: 'Não é possível inserir um produto em um documento de entrada sem seu cadastro no Catálogo do Fornecedor (Produto no Fornecedor)',
      type: 'warning'
    })
  }
}

// Handler do ConfirmationModal para alerta de tolerância de saldo
const handleToleranceConfirm = (confirmed) => {
  if (confirmed) {
    prosseguirAlocacao()
  }
}

// Watcher para detectar troca de documento e recarregar saldos autonomamente
watch(() => props.idDocumentoEntrada, (novoId, idAnterior) => {
  if (idAnterior !== undefined && novoId !== idAnterior) {
    mostrarSaldos.value = false
  }
  docAtualId.value = novoId
  // Carrega os saldos disponíveis do backend (SmartMode autônomo)
  carregarSaldosDisponiveis()
}, { immediate: true })

// Abre o painel se o documento estiver vazio na primeira carga
// E recarrega os saldos quando os itens mudam (após alocar/atualizar/remover)
watch(() => props.itensDocumento, (novaLista, listaAnterior) => {
  if (novaLista.length === 0 && !mostrarSaldos.value && docAtualId.value !== null) {
    mostrarSaldos.value = true
  }
  // Se a lista mudou (adicionou/removeu itens) e já temos um documento carregado,
  // recarrega os saldos para manter os cards atualizados
  if (listaAnterior !== undefined && novaLista !== listaAnterior && docAtualId.value !== null) {
    carregarSaldosDisponiveis()
  }
}, { immediate: true })

// Em modo saldo, o painel de saldos deve abrir automaticamente quando o
// documento está vazio — mesmo que produtosComSaldo ainda não tenha sido
// carregado (ex: cota sem produtos alocados ou carga assíncrona pendente).
watch(() => props.modoAlocacao, (modo) => {
  if (modo === 'saldo' && props.itensDocumento.length === 0) {
    mostrarSaldos.value = true
  }
}, { immediate: true })

// Quando o frame é fechado, recolhe os saldos
watch(() => props.accordionFechado, (fechado) => {
  if (fechado) {
    mostrarSaldos.value = false
  }
})

const toggleSaldos = () => {
  mostrarSaldos.value = !mostrarSaldos.value
}

// Mensagem personalizada para o ConfirmationModal de preço, conforme o contexto de origem
const mensagemPreco = computed(() => {
  const map = {
    cota: 'O preço unitário informado é superior a 10% do valor acordado na cota/contrato. Deseja realmente prosseguir com este valor?',
    embarque: 'O preço unitário informado é superior a 10% do valor de referência do embarque. Deseja realmente prosseguir com este valor?',
    container: 'O preço unitário informado é superior a 10% do valor de referência do container. Deseja realmente prosseguir com este valor?',
    ordemCompra: 'O preço unitário informado é superior a 10% do valor acordado na ordem de compra. Deseja realmente prosseguir com este valor?',
    outros: 'O preço unitário informado é superior a 10% do valor de referência. Deseja realmente prosseguir com este valor?'
  }
  return map[props.contextoOrigem] || map.outros
})

// ============================================================
// CONTROLE: produtos do cadastro (fallback sem cota)
// ============================================================
const temProdutosComSaldo = computed(() => {
  return saldosDisponiveis.value.length > 0
})

const showBuscaProdutoModal = ref(false)
const produtoSelecionadoCadastro = ref(null)

const configBuscaProduto = computed(() => criarConfigBuscaProduto())

const handleProdutoCadastroSelecionado = async (produto) => {
  if (!produto) return
  showBuscaProdutoModal.value = false

  // Prepara dados do produto para o modal de alocação
  produtoSelecionadoCadastro.value = produto

  // Buscar dados complementares do produto
  let codigoNcm = produto.codigoNcm || ''
  let controlaLote = false
  let permiteConversaoVariavel = produto.permiteConversaoVariavel || false
  let gerirEstoquePorUnidadeLogistica = produto.gerirEstoquePorUnidadeLogistica || false
  let lstUnidadeMedidaFornecedor = ''
  let lstUnidadeMedidaBasica = ''
  let lstUnidadeMedidaVolume = ''
  let fatorConversao = 0
  let lstCodigoNbs = ''
  let lstCodigoLc116 = ''

  try {
    const resProd = await api.get(`/Produtos/${produto.idProduto}`)
    const prodData = resProd.data
    codigoNcm = prodData.codigoNcm || codigoNcm
    permiteConversaoVariavel = prodData.permiteConversaoVariavel || false
    gerirEstoquePorUnidadeLogistica = prodData.gerirEstoquePorUnidadeLogistica || false
    controlaLote = await verificarControlaLote(produto.idProduto)
    lstUnidadeMedidaBasica = prodData.lstUnidadeMedidaBase || ''
    lstUnidadeMedidaVolume = prodData.lstUnidadeMedidaVolume || ''
    lstCodigoNbs = prodData.lstCodigoNbs || ''
    lstCodigoLc116 = prodData.lstCodigoLc116 || ''
  } catch (e) {
    console.error('Erro ao buscar dados do produto:', e)
  }

  // ============================================================
  // VALIDAÇÃO: Verifica se o produto possui cadastro ProdutoFornecedor
  // para o fornecedor do documento de entrada
  // SÓ executa quando modoAlocacao é 'livre' (sem saldo de cota/contrato),
  // pois em modo 'saldo' o vínculo já foi validado no cadastro do contrato
  // ============================================================
  if (props.idParceiroNegocioFornecedor && props.modoAlocacao !== 'saldo') {
    try {
      const res = await api.get('/ProdutoFornecedores', {
        params: {
          idProduto: produto.idProduto,
          idFornecedor: props.idParceiroNegocioFornecedor,
          pageSize: 1000
        }
      })

      if (!res.data || res.data.length === 0) {
        // Produto sem vínculo com o fornecedor
        emit('show-notification', {
          message: 'Produto sem cadastro no Catálogo do Fornecedor (Produto-Fornecedor).',
          type: 'warning'
        })
        // Exibe ConfirmationModal perguntando se deseja ir para o cadastro
        nextTick(() => {
          if (produtoFornecedorModalRef.value) {
            produtoFornecedorModalRef.value.show()
          }
        })
        // Interrompe o fluxo — NÃO adiciona o produto
        return
      }

      // Extrai dados de conversão do ProdutoFornecedor
      const link = res.data[0]
      lstUnidadeMedidaFornecedor = link.lstUnidadeFornecedor || ''
      fatorConversao = Number(link.fatorConversao) || 0
      lstUnidadeMedidaBasica = link.lstUnidadeMedidaBaseProduto || lstUnidadeMedidaBasica
    } catch (error) {
      console.error('Erro ao verificar vínculo ProdutoFornecedor:', error)
      emit('show-notification', {
        message: 'Erro ao verificar vínculo do produto com o fornecedor.',
        type: 'error'
      })
      return
    }
  }

  // Define saldo disponível: 100 para fallback sem cota (não trava input numérico)
  const saldoFallback = 100

  // Popula o modal de alocação
  fractionModalData.value = {
    idPurchaseOrderProduto: null,
    idOrdemCompraItem: produto.idOrdemCompraItem || null,
    idProduto: produto.idProduto,
    nomeProduto: produto.nome || produto.nomeProduto || '',
    sku: produto.sku || '',
    lstNaturezaItem: produto.lstNaturezaItem || '',
    idTipoProduto: produto.idTipoProduto || null,
    nomeTipoProduto: produto.nomeTipoProduto || '',
    lstUnidadeMedidaFornecedor: lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica: lstUnidadeMedidaBasica,
    lstUnidadeMedidaVolume: lstUnidadeMedidaVolume,
    fatorConversao: fatorConversao,
    permiteConversaoVariavel: permiteConversaoVariavel,
    gerirEstoquePorUnidadeLogistica: gerirEstoquePorUnidadeLogistica,
    precoUnitarioReferencia: 0,
    precoUnitario: 0,
    valorUnitarioOriginal: 0,
    valorUnitarioDesconto: 0,
    saldoDisponivelForn: saldoFallback,
    saldoDisponivelBase: saldoFallback,
    saldoPendenteForn: saldoFallback,
    saldoPendenteBase: saldoFallback,
    quantidadeForn: 0,
    quantidadeBase: 0,
    quantidadeVolumes: 0,
    observacao: '',
    idParceiroNegocioProdutor: props.idParceiroNegocioFornecedor,
    idLoteFabricacao: null,
    idLocalEstoque: props.idLocalEstoquePadrao,
    codigoNcm: codigoNcm,
    controlaLote: controlaLote,
    lstCodigoNbs: lstCodigoNbs,
    lstCodigoLc116: lstCodigoLc116,
    lstModalidadeRateio: null,
    lstPaisOrigem: null
  }

  // Auto-fetch da modalidade de rateio do ProdutoFornecedor (on-time)
  buscarModalidadeRateioProdutoFornecedor(produto.idProduto, props.idParceiroNegocioFornecedor).then(modalidade => {
    if (modalidade) {
      fractionModalData.value.lstModalidadeRateio = modalidade
    }
  })

  // Auto-preenche País de Origem com Brasil se for operação nacional
  if (props.ehOperacaoNacional) {
    fractionModalData.value.lstPaisOrigem = GEO_PAISES.Brasil
  }

  // Busca a flag de unidade logística do produto (habilita/bloqueia Qtd Volumes)
  if (produto.idProduto) {
    buscarDadosUnidadeLogistica(produto.idProduto)
  }

  showFractionModal.value = true
}

// Saldo real (apenas garante valores >= 0).
// O saldo já vem líquido do backend (endpoint /saldos-disponiveis),
// que centraliza o cálculo de PO e OC com tolerância.
const produtosComSaldoReal = computed(() => {
  return saldosDisponiveis.value.map(item => {
    return {
      ...item,
      qtdPendenteForn: Math.max(0, Number(item.qtdPendenteForn) || 0),
      qtdPendenteBase: Math.max(0, Number(item.qtdPendenteBase) || 0)
    }
  })
})

const produtosComSaldoOrdenados = computed(() => {
  return [...produtosComSaldoReal.value].sort((a, b) => {
    const saldoA = (Number(a.qtdPendenteForn) || 0) > 0 ? 0 : 1
    const saldoB = (Number(b.qtdPendenteForn) || 0) > 0 ? 0 : 1
    return saldoA - saldoB
  })
})

const itensDocumentoEnriquecidos = computed(() => {
  return props.itensDocumento.map(item => {
    const produtoSaldo = saldosDisponiveis.value.find(
      p => (item.idPurchaseOrderProduto && p.idPurchaseOrderProduto === item.idPurchaseOrderProduto) ||
           (item.idOrdemCompraItem && p.idOrdemCompraItem === item.idOrdemCompraItem) ||
           p.idProduto === item.idProduto
    )
    return {
      ...item,
      nomeProduto: item.nomeProduto || (produtoSaldo ? produtoSaldo.nomeProduto : '') || 'Produto #' + item.idProduto,
      sku: item.sku || (produtoSaldo ? produtoSaldo.sku : '') || ''
    }
  })
})

// ============================================================
// DRAG & DROP
// ============================================================
const draggingOverId = ref(null)

const controlaLoteCache = ref({})

const verificarControlaLote = async (idProduto) => {
  if (!idProduto) return false
  if (controlaLoteCache.value[idProduto] !== undefined) return controlaLoteCache.value[idProduto]
  try {
    const resProd = await api.get(`/Produtos/${idProduto}`)
    const idTipoProduto = resProd.data.idTipoProduto
    if (idTipoProduto) {
      const resTipo = await api.get(`/TiposProduto/${idTipoProduto}`)
      const result = !!resTipo.data.controlaLote
      controlaLoteCache.value[idProduto] = result
      return result
    }
  } catch (e) {
    console.error('Erro ao verificar ControlaLote do produto:', e)
  }
  controlaLoteCache.value[idProduto] = false
  return false
}

// ============================================================
// UNIDADE LOGÍSTICA (CATCH WEIGHT)
// Busca os dados do produto/tipe de produto para definir se o estoque
// é gerido por unidade logística (habilita o campo Qtd Volumes).
// ============================================================
const gerirUnidadeLogisticaCache = ref({})

const buscarDadosUnidadeLogistica = async (idProduto) => {
  if (!idProduto) return
  if (gerirUnidadeLogisticaCache.value[idProduto] !== undefined) {
    const cache = gerirUnidadeLogisticaCache.value[idProduto]
    fractionModalData.value.gerirEstoquePorUnidadeLogistica = cache.gerirEstoquePorUnidadeLogistica
    if (cache.lstUnidadeMedidaVolume) {
      fractionModalData.value.lstUnidadeMedidaVolume = cache.lstUnidadeMedidaVolume
    }
    return
  }
  try {
    const resProd = await api.get(`/Produtos/${idProduto}`)
    const gerir = !!resProd.data.gerirEstoquePorUnidadeLogistica
    const lstVolume = resProd.data.lstUnidadeMedidaVolume || ''
    gerirUnidadeLogisticaCache.value[idProduto] = { gerirEstoquePorUnidadeLogistica: gerir, lstUnidadeMedidaVolume: lstVolume }
    fractionModalData.value.gerirEstoquePorUnidadeLogistica = gerir
    if (lstVolume) {
      fractionModalData.value.lstUnidadeMedidaVolume = lstVolume
    }

    // Busca o fator de conversão da unidade logística (Base → Volumes)
    // via ProdutoConversaoUnidades. A conversão é sempre em função da unidade base:
    //   quantidadeVolumes = quantidadeBase / fatorConversaoVolume
    // Ex: 1 Saca (UN_MED7) = 60 Kg → fatorConversaoVolume = 60
    if (gerir && lstVolume) {
      try {
        const resConv = await api.get('/ProdutoConversaoUnidades', { params: { idProduto } })
        const conversoes = resConv.data || []
        // Procura a conversão que corresponde à unidade logística do produto
        const conv = conversoes.find(c => c.lstUnidadeMedida === lstVolume) ||
                     conversoes.find(c => c.unidadePadraoVenda) ||
                     conversoes.find(c => !c.isBase && c.fatorConversao > 0)
        if (conv && conv.fatorConversao > 0) {
          fractionModalData.value.fatorConversaoVolume = conv.fatorConversao
        }
      } catch (e) {
        console.warn('[SmartMode] Erro ao buscar fator de conversão de volumes:', e)
      }
    }
  } catch (e) {
    console.error('Erro ao verificar unidade logística do produto:', e)
    fractionModalData.value.gerirEstoquePorUnidadeLogistica = false
  }
}

// ============================================================
// AUTO-FETCH: Modalidade de Rateio via ProdutoFornecedor
// Busca a modalidade de rateio cadastrada no vínculo Produto x Fornecedor
// para preencher automaticamente o campo no modal do item (on-time).
// ============================================================
const buscarModalidadeRateioProdutoFornecedor = async (idProduto, idFornecedor) => {
  if (!idProduto || !idFornecedor) return null
  try {
    const res = await api.get('/ProdutoFornecedores', {
      params: { idProduto, idFornecedor, pageSize: 1 }
    })
    if (res.data?.length > 0 && res.data[0].lstModalidadeRateio) {
      return res.data[0].lstModalidadeRateio
    }
  } catch (e) {
    console.warn('[SmartMode] Erro ao buscar modalidade rateio do ProdutoFornecedor:', e)
  }
  return null
}

const onDragStart = (event, item) => {
  if (!props.podeEditar) {
    event.preventDefault()
    return
  }
  const data = {
    idPurchaseOrderProduto: item.idPurchaseOrderProduto,
    idOrdemCompraItem: item.idOrdemCompraItem || null,
    idProduto: item.idProduto,
    nomeProduto: item.nomeProduto || '',
    sku: item.sku || '',
    lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || '',
    fatorConversao: item.fatorConversao || 0,
    permiteConversaoVariavel: !!item.permiteConversaoVariavel,
    precoUnitarioReferencia: Number(item.precoUnitarioUnFornecedorAcordado) || 0,
    valorUnitarioOriginal: Number(item.valorUnitarioOriginal) || 0,
    valorUnitarioDesconto: Number(item.valorUnitarioDesconto) || 0,
    saldoDisponivelForn: Number(item.qtdPendenteForn) || 0,
    saldoDisponivelBase: Number(item.qtdPendenteBase) || 0,
    saldoPossivelForn: Number(item.qtdSaldoPossivelForn) || 0,
    saldoPossivelBase: Number(item.qtdSaldoPossivelBase) || 0,
    percentualTolerancia: Number(item.percentualTolerancia) || 0,
    // Total original (PO/OC) para cálculo do saldo com tolerância
    quantidadeTotalForn: Number(item.quantidadeUnFornecedorNegociada) || 0,
    quantidadeTotalBase: Number(item.quantidadeUnBasicaNegociada) || 0,
    codigoNcm: item.codigoNcm || '',
    lstCodigoNbs: item.lstCodigoNbs || '',
    lstCodigoLc116: item.lstCodigoLc116 || ''
  }
  event.dataTransfer.setData('application/json', JSON.stringify(data))
  event.dataTransfer.effectAllowed = 'move'
}

const onDragEnter = () => {
  if (!props.podeEditar) return
  draggingOverId.value = '__doc__'
}

const onDragOver = (event, id) => {
  if (!props.podeEditar) return
  event.preventDefault()
  draggingOverId.value = id
  event.dataTransfer.dropEffect = 'move'
}

const onDragLeave = (event) => {
  if (draggingOverId.value === '__doc__') {
    const related = event.relatedTarget
    if (!related || !event.currentTarget.contains(related)) {
      draggingOverId.value = null
    }
  }
}

// ============================================================
// MODAL DE ALOCAÇÃO
// ============================================================
const showFractionModal = ref(false)

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

watch(showFractionModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
  ultimoPaisOrigem.value = null
})

const fractionModalData = ref({
  idPurchaseOrderProduto: null,
  idOrdemCompraItem: null,
  idProduto: null,
  nomeProduto: '',
  sku: '',
  lstNaturezaItem: '',
  idTipoProduto: null,
  nomeTipoProduto: '',
  lstUnidadeMedidaFornecedor: '',
  lstUnidadeMedidaBasica: '',
  fatorConversao: 0,
  precoUnitarioReferencia: 0,
  precoUnitario: 0,
  valorUnitarioOriginal: 0,
  valorUnitarioDesconto: 0,
  quantidadeVolumes: 0,
  lstUnidadeMedidaVolume: '',
  gerirEstoquePorUnidadeLogistica: false,
  fatorConversaoVolume: 0,
  saldoDisponivelForn: 0,
  saldoDisponivelBase: 0,
  saldoPendenteForn: 0,
  saldoPendenteBase: 0,
  saldoPossivelForn: 0,
  saldoPossivelBase: 0,
  percentualTolerancia: 0,
  quantidadeTotalForn: 0,
  quantidadeTotalBase: 0,
  quantidadeForn: 0,
  quantidadeBase: 0,
  observacao: '',
  // NOVOS CAMPOS
  idParceiroNegocioProdutor: null,
  idLoteFabricacao: null,
  idLocalEstoque: props.idLocalEstoquePadrao,
  codigoNcm: '',
  controlaLote: false,
  permiteConversaoVariavel: false,
  lstCodigoNbs: '',
  lstCodigoLc116: '',
  lstModalidadeRateio: null,
  lstPaisOrigem: null
})

const onDrop = (event) => {
  draggingOverId.value = null
  if (!props.podeEditar) return

  editingItemId.value = null

  const rawData = event.dataTransfer.getData('application/json')
  if (!rawData) return

  try {
    const data = JSON.parse(rawData)

    fractionModalData.value = {
      idPurchaseOrderProduto: data.idPurchaseOrderProduto,
      idOrdemCompraItem: data.idOrdemCompraItem || null,
      idProduto: data.idProduto,
      nomeProduto: data.nomeProduto,
      sku: data.sku,
      lstNaturezaItem: data.lstNaturezaItem || '',
      idTipoProduto: data.idTipoProduto || null,
      nomeTipoProduto: data.nomeTipoProduto || '',
      lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
      lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
      fatorConversao: Number(data.fatorConversao) || 0,
      permiteConversaoVariavel: !!data.permiteConversaoVariavel,
      gerirEstoquePorUnidadeLogistica: false,
      lstUnidadeMedidaVolume: data.lstUnidadeMedidaVolume || '',
      quantidadeVolumes: Number(data.quantidadeVolumes) || 0,
      precoUnitarioReferencia: Number(data.precoUnitarioReferencia) || 0,
      precoUnitario: Number(data.precoUnitarioReferencia) || 0,
      valorUnitarioOriginal: Number(data.valorUnitarioOriginal) || 0,
      valorUnitarioDesconto: Number(data.valorUnitarioDesconto) || 0,
      saldoDisponivelForn: Number(data.saldoDisponivelForn) || 0,
      saldoDisponivelBase: Number(data.saldoDisponivelBase) || 0,
      saldoPendenteForn: Number(data.saldoDisponivelForn) || 0,
      saldoPendenteBase: Number(data.saldoDisponivelBase) || 0,
      saldoPossivelForn: Number(data.saldoPossivelForn) || 0,
      saldoPossivelBase: Number(data.saldoPossivelBase) || 0,
      percentualTolerancia: Number(data.percentualTolerancia) || 0,
      quantidadeTotalForn: Number(data.quantidadeTotalForn) || 0,
      quantidadeTotalBase: Number(data.quantidadeTotalBase) || 0,
      quantidadeForn: Number(data.saldoDisponivelForn) || 0,
      quantidadeBase: Number(data.saldoDisponivelBase) || 0,
      observacao: '',
      idParceiroNegocioProdutor: props.idParceiroNegocioFornecedor,
      idLoteFabricacao: null,
      idLocalEstoque: props.idLocalEstoquePadrao,
      codigoNcm: data.codigoNcm || '',
      controlaLote: false,
      lstCodigoNbs: data.lstCodigoNbs || '',
      lstCodigoLc116: data.lstCodigoLc116 || '',
      lstModalidadeRateio: null,
      lstPaisOrigem: null
    }

    // Buscar controlaLote automaticamente após o drop
    const idProd = data.idProduto
    if (idProd) {
      verificarControlaLote(idProd).then(ctrlLote => {
        fractionModalData.value.controlaLote = ctrlLote
      })
    }

    // Busca a flag de unidade logística do produto (habilita/bloqueia Qtd Volumes)
    if (idProd) {
      buscarDadosUnidadeLogistica(idProd)
    }

    // Auto-fetch da modalidade de rateio do ProdutoFornecedor (on-time)
    buscarModalidadeRateioProdutoFornecedor(data.idProduto, props.idParceiroNegocioFornecedor).then(modalidade => {
      if (modalidade) {
        fractionModalData.value.lstModalidadeRateio = modalidade
      }
    })

    // Auto-preenche País de Origem com Brasil se for operação nacional
    if (props.ehOperacaoNacional) {
      fractionModalData.value.lstPaisOrigem = GEO_PAISES.Brasil
    }

    showFractionModal.value = true
  } catch (e) {
    console.error('Erro ao processar drop:', e)
  }
}

const { simular } = useConversaoQuantidade()

// Conversão centralizada (backend = fonte da verdade).
// Chamada no lost focus do campo de quantidade ou no botão "Calcular".
const calcularConversao = async (campo) => {
  const data = fractionModalData.value
  if (!data || !data.idProduto) return

  // Limita ao saldo + tolerância (PercentualTolerancia)
  const tetoForn = limiteToleranciaForn.value
  const tetoBase = limiteToleranciaBase.value
  if (Number(data.quantidadeForn) > tetoForn) data.quantidadeForn = tetoForn
  if (Number(data.quantidadeBase) > tetoBase) data.quantidadeBase = tetoBase

  try {
    const resultado = await simular({
      idProduto: data.idProduto,
      idFornecedor: props.idParceiroNegocioFornecedor,
      quantidadeFornecedor: Number(data.quantidadeForn) || 0,
      quantidadeBase: Number(data.quantidadeBase) || 0,
      fatorConversao: Number(data.fatorConversao) || null,
      campoOrigem: campo === 'base' ? 'Base' : 'Fornecedor'
    })
    data.quantidadeForn = resultado.quantidadeFornecedor
    data.quantidadeBase = resultado.quantidadeBase
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao calcular a conversão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const ajustarQuantidade = (campo, delta) => {
  const data = fractionModalData.value
  // O teto é o saldo + tolerância (PercentualTolerancia)
  const teto = campo === 'quantidadeForn' ? limiteToleranciaForn.value : limiteToleranciaBase.value
  const atual = Number(data[campo]) || 0
  const novo = Math.min(teto, Math.max(0, atual + delta))
  data[campo] = parseFloat(novo.toFixed(4))
}

// ============================================================
// CONVERSÃO FIXA AUTOMÁTICA (espelha PurchaseOrderSmartMode)
// ============================================================
// Flag que evita loop entre os dois watches de conversão.
const isUpdatingFromConversion = ref(false)

// Watch de conversão automática: quantidadeForn → quantidadeBase
watch(() => fractionModalData.value.quantidadeForn, (novoValor) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  if (data.permiteConversaoVariavel) return
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadeBase = parseFloat((Number(novoValor) * fator).toFixed(4))
    isUpdatingFromConversion.value = false
  }
})

// Watch de conversão automática: quantidadeBase → quantidadeForn
watch(() => fractionModalData.value.quantidadeBase, (novoValor) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  if (data.permiteConversaoVariavel) return
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadeForn = parseFloat((Number(novoValor) / fator).toFixed(4))
    isUpdatingFromConversion.value = false
  }
})

// ============================================================
// CÁLCULOS AUTOMÁTICOS DE PREÇOS (espelha PurchaseOrderSmartMode)
// ============================================================
const valorTotalOriginalCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * fractionModalData.value.valorUnitarioOriginal) || 0
})

const precoUnitarioNegociadoCalc = computed(() => {
  return Math.max(0, (fractionModalData.value.valorUnitarioOriginal - fractionModalData.value.valorUnitarioDesconto) || 0)
})

const valorTotalNegociadoCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * precoUnitarioNegociadoCalc.value) || 0
})

const valorTotalDescontoCalc = computed(() => {
  return (fractionModalData.value.quantidadeForn * fractionModalData.value.valorUnitarioDesconto) || 0
})

// ============================================================
// CONVERSÃO DE VOLUMES (Base → Volumes, unidirecional)
// O fatorConversaoVolume vem do ProdutoConversaoUnidades (ex: 1 Saca = 60 Kg → fator=60).
// A conversão é sempre em função da unidade base:
//   quantidadeVolumes = quantidadeBase / fatorConversaoVolume
// Volumes NUNCA recalcula a base (unidirecional).
// ============================================================
const saldoDisponivelVolume = computed(() => {
  const data = fractionModalData.value
  const fator = Number(data.fatorConversaoVolume)
  const saldoBase = Number(data.saldoDisponivelBase) || 0
  if (fator > 0 && saldoBase > 0) return parseFloat((saldoBase / fator).toFixed(4))
  return 0
})

// ============================================================
// LIMITE DO SLIDER (com tolerância)
// O slider deve ir de 0 (min) até qtdFornAtual + saldoPossivelForn (max).
// saldoPossivelForn = QtdSaldoPossivel (já considera tolerância).
// qtdFornAtual + saldoPossivelForn = saldoDisponivelForn + (saldoPossivelForn - saldoPendenteForn)
// Fallback: se o backend não retornou (ex: modo livre), usa saldoDisponivel.
// ============================================================
const limiteToleranciaForn = computed(() => {
  const data = fractionModalData.value
  const saldoPossivel = Number(data.saldoPossivelForn) || 0
  const saldoPendente = Number(data.saldoPendenteForn) || 0
  const saldoDisponivel = Number(data.saldoDisponivelForn) || 0
  if (saldoPossivel > 0) {
    // max = saldoDisponivel + (saldoPossivel - saldoPendente) = qtdAtual + saldoPossivel
    return parseFloat(Math.max(0, saldoDisponivel + (saldoPossivel - saldoPendente)).toFixed(4))
  }
  return saldoDisponivel
})

const limiteToleranciaBase = computed(() => {
  const data = fractionModalData.value
  const saldoPossivel = Number(data.saldoPossivelBase) || 0
  const saldoPendente = Number(data.saldoPendenteBase) || 0
  const saldoDisponivel = Number(data.saldoDisponivelBase) || 0
  if (saldoPossivel > 0) {
    // max = saldoDisponivel + (saldoPossivel - saldoPendente) = qtdAtual + saldoPossivel
    return parseFloat(Math.max(0, saldoDisponivel + (saldoPossivel - saldoPendente)).toFixed(4))
  }
  return saldoDisponivel
})

const calcularConversaoVolumes = () => {
  const data = fractionModalData.value
  const fator = Number(data.fatorConversaoVolume)
  if (fator > 0) {
    data.quantidadeVolumes = parseFloat((Number(data.quantidadeBase) / fator).toFixed(4))
  }
}

const ajustarVolume = (delta) => {
  const data = fractionModalData.value
  const atual = Number(data.quantidadeVolumes) || 0
  const teto = saldoDisponivelVolume.value > 0 ? saldoDisponivelVolume.value : 999999
  const novo = Math.min(teto, Math.max(0, atual + delta))
  data.quantidadeVolumes = parseFloat(novo.toFixed(4))
}

// Mantém precoUnitario (base do motor fiscal) sincronizado com o negociado calculado,
// sempre que o usuário editar Original ou Desconto (mesmo comportamento do PurchaseOrder).
// Ignora a inicialização do modal (isSettingFractionData) para não sobrescrever dados herdados.
watch([() => fractionModalData.value.valorUnitarioOriginal, () => fractionModalData.value.valorUnitarioDesconto], () => {
  if (!showFractionModal.value || isSettingFractionData.value) return
  fractionModalData.value.precoUnitario = precoUnitarioNegociadoCalc.value
})

const confirmarAlocacao = async () => {
  const data = fractionModalData.value

  // Limpa erros de validação anteriores (preserva o Proxy — usa delete)
  localEstoqueError.value = ''
  delete formErrors.value.LstPaisOrigem

  if (data.quantidadeForn <= 0 && data.quantidadeBase <= 0) {
    emit('show-notification', { message: 'Informe pelo menos uma quantidade maior que zero.', type: 'warning' })
    return
  }

  // --- Validação de tolerância: se a quantidade ultrapassar o saldo com tolerância,
  // restaura ao limite e emite alerta ---
  if (Number(data.quantidadeTotalForn) > 0 || Number(data.quantidadeTotalBase) > 0) {
    const tetoForn = limiteToleranciaForn.value
    const tetoBase = limiteToleranciaBase.value
    let ajustou = false
    if (Number(data.quantidadeForn) > tetoForn) {
      data.quantidadeForn = tetoForn
      ajustou = true
    }
    if (Number(data.quantidadeBase) > tetoBase) {
      data.quantidadeBase = tetoBase
      ajustou = true
    }
    if (ajustou) {
      emit('show-notification', {
        message: 'A quantidade informada ultrapassa o limite com tolerância. O valor foi ajustado para o máximo permitido.',
        type: 'warning'
      })
    }
  }

  // Validação de 10% de diferença no preço unitário (só em modo saldo com referência)
  if (props.modoAlocacao === 'saldo' && data.precoUnitarioReferencia > 0) {
    const limite = data.precoUnitarioReferencia * 1.10
    if (data.precoUnitario > limite) {
      confirmationModalRef.value.show()
      return
    }
  }

  // --- Validação de tolerância de saldo (somente se vinculado a PurchaseOrderProduto) ---
  const verificarTolerancia = async () => {
    const idPoProduto = fractionModalData.value.idPurchaseOrderProduto
    if (!idPoProduto) return true // sem vínculo, permite

    const qtd = Number(fractionModalData.value.quantidadeForn) || 0
    if (qtd <= 0) return true

    try {
      const response = await api.get('/DocumentosEntradaItens/verificar-tolerancia', {
        params: {
          idPurchaseOrderProduto: idPoProduto,
          quantidade: qtd,
          // Ao editar um item existente, informa o id para que o backend
          // exclua o próprio item da soma de "já alocado" (evita falso positivo)
          idDocumentoEntradaItem: editingItemId.value || null
        },
        _suppressLoading: true
      })
      if (response.data?.excede) {
        toleranceWarningMessage.value = response.data.mensagem || 'A quantidade informada excede o saldo disponível com tolerância.'
        toleranceModalRef.value.show()
        return false // não prossegue ainda — aguarda confirmação do modal
      }
    } catch (e) {
      // Se o endpoint falhar, permite prosseguir (não bloqueia)
      console.warn('[Tolerancia] Erro ao verificar tolerância:', e)
    }
    return true
  }

  const resultadoTolerancia = await verificarTolerancia()
  if (!resultadoTolerancia) return

  // Validação de campos obrigatórios — TODOS são avaliados de uma vez para que o
  // comportamento required seja aplicado a todos os campos inválidos simultaneamente
  // (não um a um por tentativa de salvar).
  let primeiraMensagem = ''

  // Validação de País de Origem para operações internacionais
  if (!props.ehOperacaoNacional && !data.lstPaisOrigem) {
    formErrors.value.LstPaisOrigem = 'Informe o País de Origem para operações internacionais.'
    primeiraMensagem = primeiraMensagem || 'Informe o País de Origem para operações internacionais.'
  }

  // Validação de Local de Estoque para operações que movimentam estoque
  if (props.movimentaEstoque && !data.idLocalEstoque) {
    localEstoqueError.value = 'Informe o Local de Estoque para operações que movimentam estoque.'
    primeiraMensagem = primeiraMensagem || 'Informe o Local de Estoque para operações que movimentam estoque.'
  }

  // Validação de volumes para produtos com gestão por unidade logística (catch weight):
  // se a quantidade básica é > 0, a quantidade de volumes não pode ficar zerada.
  if (data.gerirEstoquePorUnidadeLogistica && Number(data.quantidadeBase) > 0 &&
      (data.quantidadeVolumes == null || Number(data.quantidadeVolumes) <= 0)) {
    primeiraMensagem = primeiraMensagem || 'A quantidade de volumes deve ser maior que zero para produtos com gestão por unidade logística.'
  }

  if (primeiraMensagem) {
    emit('show-notification', { message: primeiraMensagem, type: 'warning' })
    return
  }

  prosseguirAlocacao()
}

const prosseguirAlocacao = () => {
  const data = fractionModalData.value
  const payload = {
    idPurchaseOrderProduto: data.idPurchaseOrderProduto,
    idOrdemCompraItem: data.idOrdemCompraItem || null,
    idProduto: data.idProduto,
    nomeProduto: data.nomeProduto,
    sku: data.sku,
    lstNaturezaItem: data.lstNaturezaItem,
    idTipoProduto: data.idTipoProduto,
    nomeTipoProduto: data.nomeTipoProduto,
    quantidadeUnFornecedor: data.quantidadeForn,
    quantidadeUnBasica: data.quantidadeBase,
    quantidadeVolumes: data.quantidadeVolumes,
    lstUnidadeMedidaVolume: data.lstUnidadeMedidaVolume,
    fatorConversao: data.fatorConversao,
    lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
    precoUnitario: precoUnitarioNegociadoCalc.value,
    valorTotalLinha: parseFloat(valorTotalNegociadoCalc.value.toFixed(2)),
    // --- BREAKDOWN ORIGINAL × DESCONTO (Desconto Comercial Obtido) ---
    valorUnitarioOriginal: data.valorUnitarioOriginal,
    valorUnitarioDesconto: data.valorUnitarioDesconto,
    valorTotalOriginal: parseFloat(valorTotalOriginalCalc.value.toFixed(2)),
    valorTotalDesconto: parseFloat(valorTotalDescontoCalc.value.toFixed(2)),
    observacao: data.observacao,
    idParceiroNegocioProdutor: data.idParceiroNegocioProdutor,
    idLoteFabricacao: data.idLoteFabricacao,
    idLocalEstoque: data.idLocalEstoque,
    codigoNcm: data.codigoNcm || null,
    lstModalidadeRateio: data.lstModalidadeRateio || null,
    lstPaisOrigem: data.lstPaisOrigem || null
  }

  if (editingItemId.value) {
    emit('atualizar-item', {
      idDocumentoEntradaItem: editingItemId.value,
      ...payload
    })
  } else {
    emit('alocar-item', payload)
  }

  showFractionModal.value = false
  editingItemId.value = null
}

const fecharModal = () => {
  showFractionModal.value = false
  editingItemId.value = null
}

watch(() => showFractionModal.value, (isOpen) => {
  if (isOpen) {
    // Só dispara simulação se o documento NÃO estiver aprovado
    if (props.lstStatusDocumento !== SUP_ST_DOC_ENT.Aprovado) {
      nextTick(() => executarSimulacaoFiscal())
    }
  } else {
    simulacaoResultado.value = null
    isVisualizacaoFiscal.value = false
    // Limpa erros de validação ao fechar o modal
    localEstoqueError.value = ''
    delete formErrors.value.LstPaisOrigem
  }
})

// Re-simula quando quantidade ou preço mudam (com debounce)
watch(() => fractionModalData.value.quantidadeForn, () => {
  if (showFractionModal.value && props.lstStatusDocumento !== SUP_ST_DOC_ENT.Aprovado) agendarSimulacaoFiscal()
})
watch(() => fractionModalData.value.precoUnitario, () => {
  if (showFractionModal.value && props.lstStatusDocumento !== SUP_ST_DOC_ENT.Aprovado) agendarSimulacaoFiscal()
})

// Config de busca de parceiro produtor (PCAT2) — auto-contida no smartmode
const configBuscaParceiroPCat2 = computed(() => ({
  ...parceiroSearch,
  titulo: 'Selecionar Parceiro (Produtor/Fabricante)',
  filtrosFixos: { lstCategoria: 'PCAT2' }
}))

// Config de busca de lote dinâmica (filtra pelo parceiro produtor selecionado)
const configBuscaLoteFabricacaoDinamica = computed(() => {
  const base = { ...props.configBuscaLoteFabricacao }
  const idFornecedor = fractionModalData.value.idParceiroNegocioProdutor
  if (idFornecedor) {
    base.filtrosFixos = { ...(base.filtrosFixos || {}), idFornecedor }
  } else {
    base.filtrosFixos = { ...(base.filtrosFixos || {}), idFornecedor: -1 }
    base.titulo = 'Selecione um parceiro produtor primeiro'
  }
  return base
})

// Limpa lote ao trocar parceiro produtor (exceto durante inicialização)
watch(() => fractionModalData.value.idParceiroNegocioProdutor, (novo, antigo) => {
  if (novo !== antigo && !isSettingFractionData.value) {
    fractionModalData.value.idLoteFabricacao = null
  }
})

// Armazena o último país de origem selecionado (exceto auto-preenchimento Brasil)
// para exibir como sugestão fantasma no campo País de Origem.
watch(() => fractionModalData.value.lstPaisOrigem, (novoPais) => {
  if (novoPais) {
    delete formErrors.value.LstPaisOrigem
  }
  if (!novoPais || novoPais === GEO_PAISES.Brasil) return
  const lista = props.listas.LstPaisOrigem || []
  const item = lista.find(i => String(i.idListaClienteItem) === String(novoPais) || String(i.idListaSistemaItem) === String(novoPais))
  if (item) {
    ultimoPaisOrigem.value = item
  }
})

// Campo Local de Estoque preenchido — limpa o erro de validação
watch(() => fractionModalData.value.idLocalEstoque, (novoLocal) => {
  if (novoLocal) {
    localEstoqueError.value = ''
  }
})

// ============================================================
// HELPERS
// ============================================================
const getTextoUnidade = (id) => {
  const lista = props.listas.LstUnidadeMedidaFornecedor || props.listas.LstUnidadeMedidaBase || props.listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '0'
  const num = Number(valor)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  })
}

const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

const getTextoLista = (codigoLista, nomeLista) => {
  if (!codigoLista) return ''
  const lista = props.listas[nomeLista]
  if (!lista) return codigoLista
  const item = lista.find(u => u.idListaClienteItem === codigoLista)
  return item ? item.textoLista : codigoLista
}

const getTextoMoeda = () => {
  if (!props.listas.LstMoeda) return 'R$'
  if (!props.lstMoedaDocumento) return 'R$'
  const moeda = props.listas.LstMoeda.find(m => m.idListaClienteItem === props.lstMoedaDocumento)
  return moeda ? moeda.textoLista : 'R$'
}

const confirmarExclusao = (item) => {
  emit('remover-item', { idDocumentoEntradaItem: item.idDocumentoEntradaItem })
}

const abrirEdicao = async (itemDoc) => {
  if (!props.podeEditar) return

  editingItemId.value = itemDoc.idDocumentoEntradaItem

  // Calcular saldo disponível: quantidade atual do item + saldo pendente REAL.
  // produtosComSaldoReal já desconta os itens alocados no documento, evitando
  // que o limite fique duplicado (bruto + quantidade atual).
  // O match usa idPurchaseOrderProduto ou idOrdemCompraItem; fallback idProduto.
  const qtdFornAtual = Number(itemDoc.quantidadeUnFornecedor) || 0
  const qtdBaseAtual = Number(itemDoc.quantidadeUnBasica) || 0

  const produtoSaldo = produtosComSaldoReal.value.find(p =>
    (itemDoc.idPurchaseOrderProduto && p.idPurchaseOrderProduto === itemDoc.idPurchaseOrderProduto) ||
    (itemDoc.idOrdemCompraItem && p.idOrdemCompraItem === itemDoc.idOrdemCompraItem) ||
    p.idProduto === itemDoc.idProduto
  )
  const saldoPendenteForn = produtoSaldo ? (Number(produtoSaldo.qtdPendenteForn) || 0) : 0
  const saldoPendenteBase = produtoSaldo ? (Number(produtoSaldo.qtdPendenteBase) || 0) : 0
  const percentualTolerancia = produtoSaldo ? (Number(produtoSaldo.percentualTolerancia) || 0) : 0
  const saldoPossivelForn = produtoSaldo ? (Number(produtoSaldo.qtdSaldoPossivelForn) || 0) : 0
  const saldoPossivelBase = produtoSaldo ? (Number(produtoSaldo.qtdSaldoPossivelBase) || 0) : 0

  const saldoDisponivelForn = qtdFornAtual + saldoPendenteForn
  const saldoDisponivelBase = qtdBaseAtual + saldoPendenteBase

  isSettingFractionData.value = true
  fractionModalData.value = {
    idPurchaseOrderProduto: itemDoc.idPurchaseOrderProduto,
    idOrdemCompraItem: itemDoc.idOrdemCompraItem || null,
    idProduto: itemDoc.idProduto,
    nomeProduto: itemDoc.nomeProduto || '',
    sku: itemDoc.sku || '',
    lstNaturezaItem: itemDoc.lstNaturezaItem || '',
    idTipoProduto: itemDoc.idTipoProduto || null,
    nomeTipoProduto: itemDoc.nomeTipoProduto || '',
    lstUnidadeMedidaFornecedor: itemDoc.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: itemDoc.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(itemDoc.fatorConversao) || 0,
    permiteConversaoVariavel: false,
    gerirEstoquePorUnidadeLogistica: false,
    lstUnidadeMedidaVolume: itemDoc.lstUnidadeMedidaVolume || '',
    quantidadeVolumes: Number(itemDoc.quantidadeVolumes) || 0,
    precoUnitarioReferencia: Number(itemDoc.precoUnitario) || 0,
    precoUnitario: Number(itemDoc.precoUnitario) || 0,
    valorUnitarioOriginal: Number(itemDoc.valorUnitarioOriginal) || 0,
    valorUnitarioDesconto: Number(itemDoc.valorUnitarioDesconto) || 0,
    saldoDisponivelForn,
    saldoDisponivelBase,
    saldoPendenteForn,
    saldoPendenteBase,
    saldoPossivelForn,
    saldoPossivelBase,
    percentualTolerancia,
    quantidadeTotalForn: Number(produtoSaldo?.quantidadeUnFornecedorNegociada) || 0,
    quantidadeTotalBase: Number(produtoSaldo?.quantidadeUnBasicaNegociada) || 0,
    quantidadeForn: qtdFornAtual,
    quantidadeBase: qtdBaseAtual,
    observacao: itemDoc.observacao || '',
    idParceiroNegocioProdutor: itemDoc.idParceiroNegocioProdutor || null,
    idLoteFabricacao: itemDoc.idLoteFabricacao || null,
    idLocalEstoque: itemDoc.idLocalEstoque,
    codigoNcm: itemDoc.codigoNcm || '',
    controlaLote: false,
    lstCodigoNbs: itemDoc.lstCodigoNbs || '',
    lstCodigoLc116: itemDoc.lstCodigoLc116 || '',
    lstModalidadeRateio: itemDoc.lstModalidadeRateio || null,
    lstPaisOrigem: itemDoc.lstPaisOrigem || null,
    // === CAMPOS DE IMPOSTOS (copiados do itemDoc para exibir no modal) ===
    valorIcmsCalculado: itemDoc.valorIcmsCalculado || null,
    aliquotaIcmsAplicada: itemDoc.aliquotaIcmsAplicada || null,
    valorIcmsEfetivoCalculado: itemDoc.valorIcmsEfetivoCalculado || null,
    aliquotaIcmsEfetivaAplicada: itemDoc.aliquotaIcmsEfetivaAplicada || null,
    valorPisCalculado: itemDoc.valorPisCalculado || null,
    aliquotaPisAplicada: itemDoc.aliquotaPisAplicada || null,
    valorCofinsCalculado: itemDoc.valorCofinsCalculado || null,
    aliquotaCofinsAplicada: itemDoc.aliquotaCofinsAplicada || null,
    valorDifalCalculado: itemDoc.valorDifalCalculado || null,
    aliquotaDifalAplicada: itemDoc.aliquotaDifalAplicada || null,
    valorIpiCalculado: itemDoc.valorIpiCalculado || null,
    aliquotaIpiAplicada: itemDoc.aliquotaIpiAplicada || null,
    valorIeCalculado: itemDoc.valorIeCalculado || null,
    valorIiCalculado: itemDoc.valorIiCalculado || null,
    valorIssRetido: itemDoc.valorIssRetido || null,
    valorIrrfRetido: itemDoc.valorIrrfRetido || null,
    valorInssRetido: itemDoc.valorInssRetido || null,
    valorPisRetido: itemDoc.valorPisRetido || null,
    valorCofinsRetido: itemDoc.valorCofinsRetido || null,
    valorCsllRetida: itemDoc.valorCsllRetida || null,
    fatorImpostosAplicado: itemDoc.fatorImpostosAplicado || null,
    valorLiquidoAPagarSnapshot: itemDoc.valorLiquidoAPagarSnapshot || null
  }
  // Libera o watcher após o próximo tick
  await nextTick()
  isSettingFractionData.value = false

  // Buscar controlaLote de forma assíncrona
  const idProd = itemDoc.idProduto
  if (idProd) {
    try {
      const ctrlLote = await verificarControlaLote(idProd)
      fractionModalData.value.controlaLote = ctrlLote
    } catch (e) {
      console.error('Erro ao verificar controlaLote:', e)
    }

    // Busca a flag de unidade logística do produto (habilita/bloqueia Qtd Volumes)
    buscarDadosUnidadeLogistica(idProd)
  }

  // Auto-fetch da modalidade de rateio apenas se o item ainda não tiver uma
  if (!itemDoc.lstModalidadeRateio) {
    buscarModalidadeRateioProdutoFornecedor(itemDoc.idProduto, props.idParceiroNegocioFornecedor).then(modalidade => {
      if (modalidade) {
        fractionModalData.value.lstModalidadeRateio = modalidade
      }
    })
  }

  // Auto-preenche País de Origem com Brasil se for operação nacional e o item não tiver país definido
  if (props.ehOperacaoNacional && !itemDoc.lstPaisOrigem) {
    fractionModalData.value.lstPaisOrigem = GEO_PAISES.Brasil
  }

  showFractionModal.value = true
}
const abrirVisualizacaoFiscal = async (itemDoc) => {
  if (!itemDoc) return
  isVisualizacaoFiscal.value = true
  editingItemId.value = null

  // Busca dados de saldo do produto no painel (para obter o Total PO)
  const produtoSaldoVis = saldosDisponiveis.value.find(p =>
    (itemDoc.idPurchaseOrderProduto && p.idPurchaseOrderProduto === itemDoc.idPurchaseOrderProduto) ||
    (itemDoc.idOrdemCompraItem && p.idOrdemCompraItem === itemDoc.idOrdemCompraItem) ||
    p.idProduto === itemDoc.idProduto
  )

  // Popula o modal com os dados do item + impostos persistidos
  fractionModalData.value = {
    idPurchaseOrderProduto: itemDoc.idPurchaseOrderProduto,
    idOrdemCompraItem: itemDoc.idOrdemCompraItem || null,
    idProduto: itemDoc.idProduto,
    nomeProduto: itemDoc.nomeProduto || '',
    sku: itemDoc.sku || '',
    lstNaturezaItem: itemDoc.lstNaturezaItem || '',
    idTipoProduto: itemDoc.idTipoProduto || null,
    nomeTipoProduto: itemDoc.nomeTipoProduto || '',
    lstUnidadeMedidaFornecedor: itemDoc.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaBasica: itemDoc.lstUnidadeMedidaBasica || '',
    fatorConversao: Number(itemDoc.fatorConversao) || 0,
    permiteConversaoVariavel: false,
    gerirEstoquePorUnidadeLogistica: false,
    lstUnidadeMedidaVolume: itemDoc.lstUnidadeMedidaVolume || '',
    quantidadeVolumes: Number(itemDoc.quantidadeVolumes) || 0,
    precoUnitarioReferencia: Number(itemDoc.precoUnitario) || 0,
    precoUnitario: Number(itemDoc.precoUnitario) || 0,
    valorUnitarioOriginal: Number(itemDoc.valorUnitarioOriginal) || 0,
    valorUnitarioDesconto: Number(itemDoc.valorUnitarioDesconto) || 0,
    saldoDisponivelForn: Number(itemDoc.quantidadeUnFornecedor) || 0,
    saldoDisponivelBase: Number(itemDoc.quantidadeUnBasica) || 0,
    saldoPendenteForn: Number(itemDoc.quantidadeUnFornecedor) || 0,
    saldoPendenteBase: Number(itemDoc.quantidadeUnBasica) || 0,
    saldoPossivelForn: Number(produtoSaldoVis?.qtdSaldoPossivelForn) || 0,
    saldoPossivelBase: Number(produtoSaldoVis?.qtdSaldoPossivelBase) || 0,
    percentualTolerancia: Number(itemDoc.percentualTolerancia) || 0,
    quantidadeTotalForn: Number(produtoSaldoVis?.quantidadeUnFornecedorNegociada) || 0,
    quantidadeTotalBase: Number(produtoSaldoVis?.quantidadeUnBasicaNegociada) || 0,
    quantidadeForn: Number(itemDoc.quantidadeUnFornecedor) || 0,
    quantidadeBase: Number(itemDoc.quantidadeUnBasica) || 0,
    observacao: itemDoc.observacao || '',
    idParceiroNegocioProdutor: itemDoc.idParceiroNegocioProdutor || null,
    idLoteFabricacao: itemDoc.idLoteFabricacao || null,
    idLocalEstoque: itemDoc.idLocalEstoque,
    codigoNcm: itemDoc.codigoNcm || '',
    controlaLote: false,
    lstCodigoNbs: itemDoc.lstCodigoNbs || '',
    lstCodigoLc116: itemDoc.lstCodigoLc116 || '',
    lstModalidadeRateio: itemDoc.lstModalidadeRateio || null,
    lstPaisOrigem: itemDoc.lstPaisOrigem || null,
    // === CAMPOS DE IMPOSTOS PERSISTIDOS ===
    valorIcmsCalculado: itemDoc.valorIcmsCalculado || null,
    aliquotaIcmsAplicada: itemDoc.aliquotaIcmsAplicada || null,
    valorIcmsEfetivoCalculado: itemDoc.valorIcmsEfetivoCalculado || null,
    aliquotaIcmsEfetivaAplicada: itemDoc.aliquotaIcmsEfetivaAplicada || null,
    valorPisCalculado: itemDoc.valorPisCalculado || null,
    aliquotaPisAplicada: itemDoc.aliquotaPisAplicada || null,
    valorCofinsCalculado: itemDoc.valorCofinsCalculado || null,
    aliquotaCofinsAplicada: itemDoc.aliquotaCofinsAplicada || null,
    valorDifalCalculado: itemDoc.valorDifalCalculado || null,
    aliquotaDifalAplicada: itemDoc.aliquotaDifalAplicada || null,
    valorIpiCalculado: itemDoc.valorIpiCalculado || null,
    aliquotaIpiAplicada: itemDoc.aliquotaIpiAplicada || null,
    valorIeCalculado: itemDoc.valorIeCalculado || null,
    valorIiCalculado: itemDoc.valorIiCalculado || null,
    valorIssRetido: itemDoc.valorIssRetido || null,
    valorIrrfRetido: itemDoc.valorIrrfRetido || null,
    valorInssRetido: itemDoc.valorInssRetido || null,
    valorPisRetido: itemDoc.valorPisRetido || null,
    valorCofinsRetido: itemDoc.valorCofinsRetido || null,
    valorCsllRetida: itemDoc.valorCsllRetida || null,
    fatorImpostosAplicado: itemDoc.fatorImpostosAplicado || null,
    valorLiquidoAPagarSnapshot: itemDoc.valorLiquidoAPagarSnapshot || null
  }

  // Busca a flag de unidade logística do produto (habilita/bloqueia Qtd Volumes)
  if (itemDoc.idProduto) {
    buscarDadosUnidadeLogistica(itemDoc.idProduto)
  }

  showFractionModal.value = true
}

</script>

<template>
  <div class="documento-itens-smart-mode">
    <!-- ============================================================ -->
    <!-- PAINEL DE SALDOS (colapsável, à esquerda) — só aparece quando há produtos da cota -->
    <!-- ============================================================ -->
    <div v-if="props.modoAlocacao === 'saldo'" class="saldos-panel" :class="{ 'saldos-visible': mostrarSaldos }">
      <div class="saldos-panel-inner">
        <div class="saldos-header">
          <div class="saldos-header-top">
            <h5 class="mb-0 d-flex align-items-center gap-2">
              <i class="bi bi-box-seam"></i>
              Produtos Disponíveis
              <span v-if="saldosDisponiveis.length > 0"
                class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle ms-1">
                {{ saldosDisponiveis.length }}
              </span>
            </h5>
            <button type="button" class="btn btn-sm btn-outline-secondary border-0 p-1" title="Fechar painel de saldos"
              @click="toggleSaldos">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <small class="text-muted">Arraste cards para alocar no documento</small>
        </div>
        <div class="saldos-body">
          <template v-if="saldosDisponiveis.length === 0">
            <div class="empty-state">
              <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
              <p class="mb-0">Nenhum saldo pendente.</p>
            </div>
          </template>
          <template v-else>
            <div v-for="item in produtosComSaldoOrdenados" :key="item.idPurchaseOrderProduto || item.idOrdemCompraItem || item.idProduto" draggable="true"
              @dragstart="onDragStart($event, item)" class="draggable-card card-saldo-produto"
              :class="{ 'card-saldo-esgotado': (Number(item.qtdPendenteForn) || 0) <= 0 }">
              <div class="card-sku" v-if="item.sku">
                <code>{{ item.sku }}</code>
              </div>
              <div class="card-title">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</div>
              <div class="card-saldo">
                <div class="saldo-row">
                  <span class="saldo-label">Saldo (Forn):</span>
                  <span class="saldo-value">{{ formatarQtd(item.qtdPendenteForn) }} {{
                    getTextoUnidade(item.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="saldo-row">
                  <span class="saldo-label">Saldo (Base):</span>
                  <span class="saldo-value">{{ formatarQtd(item.qtdPendenteBase) }} {{
                    getTextoUnidade(item.lstUnidadeMedidaBasica) }}</span>
                </div>
              </div>
              <div class="card-preco" v-if="Number(item.precoUnitarioUnFornecedorAcordado) > 0">
                <span class="saldo-label">Preço Unit. Acordado:</span>
                <span class="saldo-value">{{ formatarDecimal(item.precoUnitarioUnFornecedorAcordado) }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- COLUNA PRINCIPAL: PRODUTOS NO DOCUMENTO DE ENTRADA -->
    <!-- ============================================================ -->
    <div class="itens-main" :class="{ 'itens-main-expanded': !mostrarSaldos }">
      <div class="itens-main-toolbar">
        <h5 class="mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-list-check"></i>
          Produtos no Documento de Entrada
          <span v-if="itensDocumento.length > 0"
            class="badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle ms-1">
            {{ itensDocumento.length }} {{ itensDocumento.length === 1 ? 'produto' : 'produtos' }}
          </span>
        </h5>
        <div class="d-flex gap-2 align-items-center">
          <!-- Botão p/ abrir painel de saldos (quando opera por saldo: cota, OC, embarque, container) -->
          <button v-if="props.modoAlocacao === 'saldo' && podeEditar" type="button" class="btn btn-sm btn-info text-white fw-bold"
            @click="toggleSaldos" :title="mostrarSaldos ? 'Fechar saldos' : 'Adicionar Produtos ao Documento'">
            <i class="bi" :class="mostrarSaldos ? 'bi-x-lg' : 'bi-plus-lg'"></i>
            {{ mostrarSaldos ? 'Fechar Saldos' : 'Adicionar Produtos ao Documento' }}
          </button>
          <!-- Botão p/ buscar produto do cadastro (somente em modo livre, sem vínculo de saldo) -->
          <button v-if="props.modoAlocacao !== 'saldo' && !temProdutosComSaldo && podeEditar" type="button" class="btn btn-sm btn-info text-white fw-bold"
            @click="showBuscaProdutoModal = true"
            title="Adicionar Produto do Cadastro">
            <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
          </button>
        </div>
      </div>

      <!-- ModalDeBusca para selecionar produto do cadastro (somente modo livre, sem vínculo de saldo) -->
      <ModalDeBusca
        v-if="showBuscaProdutoModal && props.modoAlocacao !== 'saldo' && !temProdutosComSaldo"
        :configuracao="configBuscaProduto"
        tamanho="lg"
        @item-selecionado="handleProdutoCadastroSelecionado"
        @fechar="showBuscaProdutoModal = false"
      />

      <div class="itens-main-body" :class="{
        'dropzone-ativa': mostrarSaldos,
        'drag-over': draggingOverId === '__doc__'
      }" :style="mostrarSaldos ? { minHeight: '200px' } : {}" @dragenter="mostrarSaldos && onDragEnter($event)"
        @dragover.prevent="mostrarSaldos && onDragOver($event, '__doc__')"
        @dragleave="mostrarSaldos && onDragLeave($event)" @drop.prevent="mostrarSaldos && onDrop($event)">

        <template v-if="itensDocumento.length === 0">
          <!-- Modo saldo: área de drag-drop com produtos do painel de saldos -->
          <div v-if="mostrarSaldos && props.modoAlocacao === 'saldo'" class="empty-state">
            <i class="bi bi-download fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Arraste produtos do painel ao lado</p>
          </div>
          <!-- Modo livre: sem saldo, apenas instrução para adicionar do cadastro -->
          <div v-else-if="mostrarSaldos && props.modoAlocacao !== 'saldo'" class="empty-state">
            <i class="bi bi-plus-circle fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Adicione produtos manualmente</p>
            <button v-if="podeEditar" type="button"
              class="btn btn-sm btn-info text-white fw-bold mt-3" @click="showBuscaProdutoModal = true">
              <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
            </button>
          </div>
          <!-- Painel recolhido: estado vazio padrão -->
          <div v-else class="empty-state">
            <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum produto alocado neste documento.</p>
            <!-- Botão p/ abrir painel de saldos (quando opera por saldo) -->
            <button v-if="props.modoAlocacao === 'saldo' && podeEditar" type="button"
              class="btn btn-sm btn-info text-white fw-bold mt-3" @click="toggleSaldos">
              <i class="bi bi-plus-lg me-1"></i> Adicionar Produtos ao Documento
            </button>
            <!-- Botão p/ buscar produto do cadastro (somente modo livre, sem vínculo de saldo) -->
            <button v-if="props.modoAlocacao !== 'saldo' && !temProdutosComSaldo && podeEditar" type="button"
              class="btn btn-sm btn-info text-white fw-bold mt-3" @click="showBuscaProdutoModal = true">
              <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
            </button>
          </div>
        </template>

        <template v-if="itensDocumentoEnriquecidos.length > 0">
          <div class="itens-grid">
            <div v-for="itemDoc in itensDocumentoEnriquecidos" :key="itemDoc.idDocumentoEntradaItem"
              class="item-card">
              <div class="item-card-header">
                <div class="item-card-info">
                  <div class="item-card-nome">
                    <span class="badge natureza-badge me-2"
                          :class="getNaturezaItem(itemDoc) === 'servico'
                            ? 'bg-success-subtle text-success border border-success-subtle'
                            : 'bg-primary-subtle text-primary border border-primary-subtle'">
                      <i class="bi me-1"
                         :class="getNaturezaItem(itemDoc) === 'servico'
                           ? 'bi-gear'
                           : 'bi-box-seam'"></i>
                      {{ getNaturezaItem(itemDoc) === 'servico' ? 'Serviço' : 'Produto Físico' }}
                    </span>
                    <code class="sku-destaque" v-if="itemDoc.sku">{{ itemDoc.sku }}</code>
                    <span class="produto-nome-texto">{{ itemDoc.nomeProduto || 'Produto #' + itemDoc.idProduto }}</span>
                  </div>
                </div>
                <div class="item-card-actions">
                  <!-- Botão de visualização fiscal (documento aprovado) -->
                  <button v-if="lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado" type="button"
                    class="btn btn-sm btn-outline-secondary border-0" title="Visualizar impostos do produto"
                    @click="abrirVisualizacaoFiscal(itemDoc)">
                    <i class="bi bi-eye"></i>
                  </button>
                  <!-- Botões de edição (documento não aprovado) -->
                  <template v-if="podeEditar">
                    <button type="button" class="btn btn-sm btn-outline-info border-0" title="Editar alocação do produto"
                      @click="abrirEdicao(itemDoc)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-danger border-0"
                      title="Remover produto do documento" @click="confirmarExclusao(itemDoc)">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </template>
                </div>
              </div>

              <!-- Códigos Fiscais (apenas NCM para Produto Físico; Serviço não exibe) -->
              <div v-if="getNaturezaItem(itemDoc) !== 'servico'" class="item-card-codigos-fiscais">
                <span v-if="itemDoc.codigoNcm" class="codigo-fiscal-badge">
                  NCM: <code>{{ itemDoc.codigoNcm }}</code>
                </span>
              </div>

              <div class="item-card-detalhes">
                <div class="detalhe-item">
                  <span class="detalhe-label">Qtd (Forn):</span>
                  <span class="detalhe-value">{{ formatarQtd(itemDoc.quantidadeUnFornecedor) }} {{
                    getTextoUnidade(itemDoc.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div class="detalhe-item">
                  <span class="detalhe-label">Qtd (Base):</span>
                  <span class="detalhe-value">{{ formatarQtd(itemDoc.quantidadeUnBasica) }} {{
                    getTextoUnidade(itemDoc.lstUnidadeMedidaBasica) }}</span>
                </div>
                <div class="detalhe-item">
                  <span class="detalhe-label">Preço Unit.:</span>
                  <span class="detalhe-value">{{ formatarDecimal(itemDoc.precoUnitario) }}</span>
                </div>
                <div class="detalhe-item">
                  <span class="detalhe-label">Preço Unit. Orig.:</span>
                  <span class="detalhe-value">{{ formatarDecimal(itemDoc.valorUnitarioOriginal) }}</span>
                </div>
                <div v-if="Number(itemDoc.valorUnitarioDesconto || 0) > 0" class="detalhe-item">
                  <span class="detalhe-label">Desc. Unit.:</span>
                  <span class="detalhe-value text-danger">-{{ formatarDecimal(itemDoc.valorUnitarioDesconto) }}</span>
                </div>
                <div class="detalhe-item detalhe-total">
                  <span class="detalhe-label">Total:</span>
                  <span class="detalhe-value fw-bold">{{ formatarDecimal(itemDoc.valorTotalLinha) }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL DE ALOCAÇÃO -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <div v-if="showFractionModal" class="modal-overlay" :style="{ zIndex: zIndexEfetivo }" @click.self="fecharModal">
        <div class="modal-card" @keyup.enter="confirmarAlocacao">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi" :class="isVisualizacaoFiscal ? 'bi-eye' : (editingItemId ? 'bi-pencil' : 'bi-arrows-split')"></i>
              <span class="me-1">{{ isVisualizacaoFiscal ? 'Visualizar Impostos' : (editingItemId ? 'Editar' : 'Alocar') }}</span>
              {{ isVisualizacaoFiscal ? 'do Produto' : 'Produto no Documento' }}
            </h5>
            <button type="button" class="btn-close" @click="fecharModal"></button>
          </div>

          <div class="modal-body">
            <!-- Info de Origem — full width -->
            <div class="modal-info-box">
              <div class="info-left">
                <div class="info-item produto-info-item">
                  <span class="info-label">Produto:</span>
                  <span class="info-valor fw-bold">
                    <template v-if="fractionModalData.sku">
                      <code class="sku-destaque d-block">{{ fractionModalData.sku }}</code>
                    </template>
                    {{ fractionModalData.nomeProduto }}
                  </span>
                </div>

                <!-- Código Fiscal: NCM para ProdutoFisico, NBS/LC116 para Serviço -->
                <template v-if="getNaturezaItem(fractionModalData) === 'servico'">
                  <div class="info-item">
                    <span class="info-label">NBS:</span>
                    <span class="info-valor fw-bold">
                      <code v-if="fractionModalData.lstCodigoNbs" style="font-size:0.75rem;">{{ getTextoLista(fractionModalData.lstCodigoNbs, 'LstCodigoNbs') }}</code>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">LC116:</span>
                    <span class="info-valor fw-bold">
                      <code v-if="fractionModalData.lstCodigoLc116" style="font-size:0.75rem;">{{ getTextoLista(fractionModalData.lstCodigoLc116, 'LstCodigoLc116') }}</code>
                    </span>
                  </div>
                </template>
                <template v-else>
                  <div class="info-item">
                    <span class="info-label">NCM:</span>
                    <span class="info-valor fw-bold">
                      <code v-if="fractionModalData.codigoNcm" style="font-size:0.75rem;">{{ fractionModalData.codigoNcm }}</code>
                    </span>
                  </div>
                </template>
              </div>

              <div class="info-right">
                <div v-if="getNaturezaItem(fractionModalData) !== 'servico'" class="info-item">
                  <span class="info-label">Saldo Disponível (Unidade do Fornecedor):</span>
                  <span class="info-valor">{{ formatarQtd(fractionModalData.saldoPendenteForn) }} {{
                    getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }}</span>
                </div>
                <div v-if="getNaturezaItem(fractionModalData) !== 'servico'" class="info-item">
                  <span class="info-label">Saldo Disponível (Unidade Base):</span>
                  <span class="info-valor">{{ formatarQtd(fractionModalData.saldoPendenteBase) }} {{
                    getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }}</span>
                </div>
              </div>
            </div>

            <!-- Grid: left 3 cols (fator conversão + sliders) + right 6 cols (preços/campos/impostos) -->
            <div class="row g-2">
              <!-- ============================================================ -->
              <!-- ÁREA ESQUERDA — 3 colunas: Fator de Conversão + Sliders -->
              <!-- ============================================================ -->
              <div class="col-4">
                <!-- Aviso de conversão — card -->
                <div v-if="!fractionModalData.permiteConversaoVariavel && Number(fractionModalData.fatorConversao) > 0"
                  class="alert alert-info py-2 mb-2 slider-section"
                  style="font-size:0.8rem;">
                  <i class="bi bi-info-circle me-2"></i>
                  Fator de conversão: {{ fractionModalData.fatorConversao }}. As quantidades são sincronizadas automaticamente.
                </div>
                <div
                  v-else-if="fractionModalData.permiteConversaoVariavel || Number(fractionModalData.fatorConversao) === 0"
                  class="alert alert-warning py-2 mb-2 slider-section"
                  style="font-size:0.8rem;">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  Este produto possui conversão variável ou não possui fator definido. As quantidades entre as unidades podem ser informadas independentemente.
                </div>

                <!-- Card Qtd Fornecedor -->
                <div class="slider-section">
                  <div class="slider-section-header">
                    <i class="bi bi-box-seam me-1"></i>
                    Qtd. Fornecedor
                    <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }})</small>
                  </div>
                  <div class="slider-row">
                    <input type="range" class="form-range slider-row-main" :min="0"
                      :max="limiteToleranciaForn" step="1"
                      v-model.number="fractionModalData.quantidadeForn" />
                    <div class="slider-row-controls">
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Diminuir 1 unidade" @click="ajustarQuantidade('quantidadeForn', -1)"
                          :disabled="fractionModalData.quantidadeForn < 1">
                          <i class="bi bi-dash-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Diminuir 0,1" @click="ajustarQuantidade('quantidadeForn', -0.1)"
                          :disabled="fractionModalData.quantidadeForn < 0.1">
                          <i class="bi bi-dash-lg"></i> 0,1
                        </button>
                      </div>
                      <div class="slider-row-value">
                        <input type="number" class="form-control form-control-sm" :min="0" step="0.01"
                          :max="limiteToleranciaForn"
                          v-model.number="fractionModalData.quantidadeForn"
                          @blur="calcularConversao('fornecedor')" />
                        <span class="slider-max">/ {{ formatarQtd(limiteToleranciaForn) }}</span>
                      </div>
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Aumentar 1 unidade" @click="ajustarQuantidade('quantidadeForn', 1)"
                          :disabled="fractionModalData.quantidadeForn >= limiteToleranciaForn">
                          <i class="bi bi-plus-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Aumentar 0.1" @click="ajustarQuantidade('quantidadeForn', 0.1)"
                          :disabled="fractionModalData.quantidadeForn >= limiteToleranciaForn">
                          <i class="bi bi-plus-lg"></i> 0,1
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Card Qtd Básica -->
                <div class="slider-section">
                  <div class="slider-section-header">
                    <i class="bi bi-box me-1"></i>
                    Qtd. Básica
                    <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }})</small>
                    <button
                      type="button"
                      class="btn btn-sm calcular-btn"
                      title="Calcular conversão de unidades"
                      :disabled="!props.podeEditar"
                      @click="calcularConversao('base')"
                    >
                      <i class="bi bi-calculator me-1"></i> Calcular
                    </button>
                  </div>
                  <div class="slider-row">
                    <input type="range" class="form-range slider-row-main" :min="0"
                      :max="limiteToleranciaBase" step="1"
                      v-model.number="fractionModalData.quantidadeBase" />
                    <div class="slider-row-controls">
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Diminuir 1 unidade" @click="ajustarQuantidade('quantidadeBase', -1)"
                          :disabled="fractionModalData.quantidadeBase < 1">
                          <i class="bi bi-dash-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Diminuir 0,1" @click="ajustarQuantidade('quantidadeBase', -0.1)"
                          :disabled="fractionModalData.quantidadeBase < 0.1">
                          <i class="bi bi-dash-lg"></i> 0,1
                        </button>
                      </div>
                      <div class="slider-row-value">
                        <input type="number" class="form-control form-control-sm" :min="0" step="0.01"
                          :max="limiteToleranciaBase"
                          v-model.number="fractionModalData.quantidadeBase"
                          @blur="calcularConversao('base')" />
                        <span class="slider-max">/ {{ formatarQtd(limiteToleranciaBase) }}</span>
                      </div>
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Aumentar 1 unidade" @click="ajustarQuantidade('quantidadeBase', 1)"
                          :disabled="fractionModalData.quantidadeBase >= limiteToleranciaBase">
                          <i class="bi bi-plus-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Aumentar 0.1" @click="ajustarQuantidade('quantidadeBase', 0.1)"
                          :disabled="fractionModalData.quantidadeBase >= limiteToleranciaBase">
                          <i class="bi bi-plus-lg"></i> 0,1
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Card Qtd Volumes -->
                <div class="slider-section">
                  <div class="slider-section-header">
                    <i class="bi bi-boxes me-1"></i>
                    Qtd. Volumes (Unidade Logística)
                    <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaVolume) }})</small>
                    <button
                      type="button"
                      class="btn btn-sm calcular-btn"
                      title="Calcular volumes a partir da base"
                      :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar"
                      @click="calcularConversaoVolumes"
                    >
                      <i class="bi bi-calculator me-1"></i> Calcular
                    </button>
                  </div>
                  <div class="slider-row">
                    <input type="range" class="form-range slider-row-main" :min="0"
                      :max="saldoDisponivelVolume" step="1"
                      v-model.number="fractionModalData.quantidadeVolumes"
                      :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar" />
                    <div class="slider-row-controls">
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Diminuir 1 unidade" @click="ajustarVolume(-1)"
                          :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar || fractionModalData.quantidadeVolumes < 1">
                          <i class="bi bi-dash-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Diminuir 0,1" @click="ajustarVolume(-0.1)"
                          :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar || fractionModalData.quantidadeVolumes < 0.1">
                          <i class="bi bi-dash-lg"></i> 0,1
                        </button>
                      </div>
                      <div class="slider-row-value">
                        <input type="number" class="form-control form-control-sm" :min="0" step="0.01"
                          :max="saldoDisponivelVolume"
                          v-model.number="fractionModalData.quantidadeVolumes"
                          :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar" />
                        <span v-if="saldoDisponivelVolume > 0" class="slider-max">/ {{ formatarQtd(saldoDisponivelVolume) }}</span>
                      </div>
                      <div class="slider-row-buttons">
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                          title="Aumentar 1 unidade" @click="ajustarVolume(1)"
                          :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar || fractionModalData.quantidadeVolumes >= saldoDisponivelVolume">
                          <i class="bi bi-plus-lg"></i> 1
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                          title="Aumentar 0.1" @click="ajustarVolume(0.1)"
                          :disabled="!fractionModalData.gerirEstoquePorUnidadeLogistica || !props.podeEditar || fractionModalData.quantidadeVolumes >= saldoDisponivelVolume">
                          <i class="bi bi-plus-lg"></i> 0,1
                        </button>
                      </div>
                    </div>
                  </div>
                  <!-- Aviso: catch-weight exige volumes > 0 quando a quantidade básica é > 0 -->
                  <div
                    v-if="fractionModalData.gerirEstoquePorUnidadeLogistica && Number(fractionModalData.quantidadeBase) > 0 && (fractionModalData.quantidadeVolumes == null || Number(fractionModalData.quantidadeVolumes) <= 0)"
                    class="text-warning small mt-1"
                  >
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    Quantidade de volumes é obrigatória para este produto (gestão por unidade logística).
                  </div>
                </div>
              </div>

              <!-- ============================================================ -->
              <!-- ÁREA DIREITA — 6 colunas: cada componente ocupa 3 colunas -->
              <!-- ============================================================ -->
              <div class="col-8">
                <div class="row g-2">
                  <!-- Valor Unit. Original -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-valorUnitarioOriginal"
                      :label="'Valor Unit. Original (' + getTextoMoeda() + ')'"
                      v-model="fractionModalData.valorUnitarioOriginal"
                      type="moeda"
                      :disabled="!props.podeEditar"
                    />
                  </div>
                  <!-- Desconto Unitário -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-valorUnitarioDesconto"
                      :label="'Desconto Unitário (' + getTextoMoeda() + ')'"
                      v-model="fractionModalData.valorUnitarioDesconto"
                      type="moeda"
                      :disabled="!props.podeEditar"
                    />
                  </div>
                  <!-- Preço Unit. Negociado -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-precoUnitarioNegociado"
                      :label="'Preço Unit. Negociado (' + getTextoMoeda() + ')'"
                      :model-value="precoUnitarioNegociadoCalc"
                      type="moeda"
                      disabled
                    />
                  </div>
                  <!-- Valor Total Original -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-valorTotalOriginal"
                      :label="'Valor Total Original (' + getTextoMoeda() + ')'"
                      :model-value="valorTotalOriginalCalc"
                      type="moeda"
                      disabled
                    />
                  </div>
                  <!-- Total Desconto -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-valorTotalDesconto"
                      :label="'Total Desconto (' + getTextoMoeda() + ')'"
                      :model-value="valorTotalDescontoCalc"
                      type="moeda"
                      disabled
                    />
                  </div>
                  <!-- Total Negociado -->
                  <div class="col-4">
                    <CampoInput
                      id="modal-valorTotalNegociado"
                      :label="'Total Negociado (' + getTextoMoeda() + ')'"
                      :model-value="valorTotalNegociadoCalc"
                      type="moeda"
                      disabled
                    />
                  </div>
                  <!-- Parceiro Produtor -->
                  <div v-if="habilitarParceiroProdutor" class="col-4">
                    <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">Parceiro
                      (Produtor/Fabricante)</label>
                    <CampoBusca id="modal-idParceiroNegocioProdutor" v-model="fractionModalData.idParceiroNegocioProdutor"
                      :configuracao-busca="configBuscaParceiroPCat2" :disabled="!props.podeEditar"
                      z-index-modal-busca="10070" hide-label />
                  </div>
                  <!-- Lote de Fabricação -->
                  <div v-if="habilitarParceiroProdutor && fractionModalData.controlaLote" class="col-4">
                    <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                      Lote de Fabricação
                      <i v-if="!fractionModalData.idParceiroNegocioProdutor" class="bi bi-info-circle text-muted ms-1"
                        title="Selecione um parceiro produtor primeiro para habilitar a busca de lotes"
                        style="cursor:help;font-size:0.75rem;"></i>
                    </label>
                    <CampoBusca id="modal-idLoteFabricacao" v-model="fractionModalData.idLoteFabricacao"
                      :configuracao-busca="configBuscaLoteFabricacaoDinamica"
                      :disabled="!props.podeEditar || !fractionModalData.idParceiroNegocioProdutor"
                      z-index-modal-busca="10070" hide-label />
                  </div>
                  <!-- Local de Estoque -->
                  <div v-if="movimentaEstoque && configBuscaLocalEstoque" class="col-4">
                    <label class="form-label fw-bold text-secondary mb-0" :class="{ 'is-required': movimentaEstoque }" style="font-size: 0.8rem;">
                      Local de Estoque
                    </label>
                    <CampoBusca id="modal-idLocalEstoque" v-model="fractionModalData.idLocalEstoque"
                      :configuracao-busca="configBuscaLocalEstoque"
                      :disabled="!props.podeEditar"
                      :required="movimentaEstoque"
                      :error="localEstoqueError"
                      z-index-modal-busca="10070" hide-label />
                  </div>
                  <!-- Modalidade Rateio -->
                  <div v-if="props.exibirModalidadeRateio" class="col-4">
                    <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                      Modalidade Rateio
                    </label>
                    <CampoBuscaLista
                      id="LstModalidadeRateio"
                      label=""
                      v-model="fractionModalData.lstModalidadeRateio"
                      tabela="DocumentoEntradaItem"
                      :disabled="true"
                      hide-label
                    />
                  </div>
                  <!-- País de Origem -->
                  <div class="col-4">
                    <label class="form-label fw-bold text-secondary mb-0" :class="{ 'is-required': !props.ehOperacaoNacional }" style="font-size: 0.8rem;">
                      País de Origem
                    </label>
                    <CampoBuscaLista
                      id="LstPaisOrigem"
                      label=""
                      v-model="fractionModalData.lstPaisOrigem"
                      tabela="DocumentoEntradaItem"
                      :disabled="!props.podeEditar || (props.ehOperacaoNacional && fractionModalData.lstPaisOrigem === GEO_PAISES.Brasil)"
                      :required="!props.ehOperacaoNacional"
                      :ultimas-selecoes="ultimoPaisOrigem ? [ultimoPaisOrigem] : null"
                      hide-label
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- RODAPÉ FULL WIDTH: Impostos (simulação em tempo real) -->
            <!-- ============================================================ -->
            <div class="impostos-modal-section">
              <div v-if="simulacaoLoading" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-info me-2"></div>
                Calculando impostos...
              </div>
              <FiscalPreviewCard
                v-else-if="fiscalItemDisplayData"
                :titulo="simulacaoResultado ? 'Prévia dos Impostos' : 'Impostos Calculados'"
                :origem="fiscalItemDisplayData._origem"
                :loading="false"
                :total-itens="1"
                :itens-com-fiscal="fiscalItemDisplayData.totalImpostos > 0 ? 1 : 0"
                :impostos="fiscalItemImpostosVisiveis"
                :total-impostos="fiscalItemDisplayData.totalImpostos"
                :valor-liquido="fiscalItemDisplayData.valorLiquido"
                :valor-total="fiscalItemDisplayData.valorTotal"
                :fator-impostos="fiscalItemDisplayData.fatorImpostos"
              />
              <div v-else class="text-center py-2 text-muted small">
                <i class="bi bi-calculator me-2"></i>
                Preencha quantidade e preço para ver a prévia dos impostos.
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <template v-if="isVisualizacaoFiscal">
              <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
                Fechar
              </button>
            </template>
            <template v-else>
              <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
                Cancelar
              </button>
              <button type="button" class="btn btn-info text-white fw-bold" @click="confirmarAlocacao" autofocus>
                <i class="bi bi-check-circle me-1"></i>
                {{ editingItemId ? 'Salvar Alterações' : 'Confirmar Alocação' }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <ConfirmationModal ref="confirmationModalRef" title="Diferença de Preço Detectada"
        :message="mensagemPreco"
        confirm-button-text="Sim, prosseguir" cancel-button-text="Não, ajustar preço"
        @confirmed="(confirmed) => confirmed && prosseguirAlocacao()" />
    </Teleport>

    <!-- ConfirmationModal para alerta de tolerância de saldo -->
    <Teleport to="body">
      <ConfirmationModal
        ref="toleranceModalRef"
        title="Atenção: Tolerância de Saldo Excedida"
        :message="toleranceWarningMessage"
        confirm-button-text="Sim, prosseguir mesmo assim"
        cancel-button-text="Não, ajustar quantidade"
        @confirmed="handleToleranceConfirm" />
    </Teleport>

    <!-- ConfirmationModal para Produto sem cadastro no Fornecedor -->
    <Teleport to="body">
      <ConfirmationModal
        ref="produtoFornecedorModalRef"
        title="Produto sem Cadastro no Fornecedor"
        message="Este produto não possui cadastro no Catálogo do Fornecedor (Produto-Fornecedor). Deseja ir para o cadastro?"
        confirm-button-text="Sim, ir para cadastro"
        cancel-button-text="Não, cancelar"
        @confirmed="handleProdutoFornecedorConfirm"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.documento-itens-smart-mode {
  position: relative;
  display: flex;
  gap: 0;
  min-height: 300px;
  align-items: flex-start;
}

.saldos-panel {
  width: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s ease;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  height: 100%;
  min-height: 300px;
}

.saldos-panel.saldos-visible {
  width: 380px;
  margin-right: 16px;
}

.saldos-panel-inner {
  width: 380px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;
}

.saldos-header {
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
  flex-shrink: 0;
}

.saldos-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.saldos-header h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.saldos-header small {
  font-size: 0.7rem;
}

.saldos-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  max-height: 500px;
}

.itens-main {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  background: var(--bs-body-bg);
  overflow: hidden;
}

.itens-main-expanded {
  width: 100%;
}

.itens-main-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
}

.itens-main-toolbar h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.itens-main-body {
  padding: 12px;
  overflow-y: auto;
  max-height: 500px;
  position: relative;
}

.itens-main-body.dropzone-ativa {
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  margin: 8px;
  transition: all 0.3s ease;
  min-height: 200px;
}

.itens-main-body.dropzone-ativa.drag-over {
  border-color: var(--bs-info);
  background-color: var(--bs-info-bg-subtle);
  transform: scale(1.01);
}

.itens-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.item-card {
  padding: 10px 12px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-success);
  border-radius: 6px;
  background: var(--bs-body-bg);
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: var(--bs-secondary-color);
}

.empty-state p {
  font-size: 0.85rem;
}

.draggable-card {
  cursor: grab;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-info);
  border-radius: 6px;
  background: var(--bs-body-bg);
  transition: transform 0.2s, box-shadow 0.2s;
}

.draggable-card:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.draggable-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-saldo-esgotado {
  opacity: 0.5;
  border-left-color: var(--bs-danger);
  cursor: not-allowed;
}

.card-title {
  font-weight: 600;
  font-size: 0.75rem;
  color: var(--bs-body-color);
  margin-bottom: 2px;
}

.card-sku {
  font-size: 0.95rem;
  margin-bottom: 6px;
  color: var(--bs-danger);
}

.card-saldo {
  font-size: 0.75rem;
}

.saldo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.saldo-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.saldo-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.card-preco {
  font-size: 0.72rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

.sku-destaque {
  color: var(--bs-danger);
  font-weight: 700;
}

.item-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

/* Natureza Badge */
.natureza-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 8px;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
}

/* Códigos Fiscais */
.item-card-codigos-fiscais {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.codigo-fiscal-badge {
  font-size: 0.65rem;
  background: var(--bs-tertiary-bg);
  padding: 1px 6px;
  border-radius: 4px;
  color: var(--bs-secondary-color);
}
.codigo-fiscal-badge code {
  font-weight: 600;
  color: var(--bs-info-text-emphasis);
  font-size: 0.7rem;
}

/* Impostos Toggle */
.imposto-toggle {
  font-size: 0.7rem;
  padding: 4px 8px;
}

.item-card-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.item-card-info {
  flex: 1;
  min-width: 0;
}

.item-card-nome {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.item-card-nome .natureza-badge {
  font-size: 0.65rem;
  padding: 0.2rem 0.4rem;
  white-space: nowrap;
}

.item-card-nome .sku-destaque {
  font-size: 0.75rem;
}

.item-card-nome .produto-nome-texto {
  font-weight: 600;
  font-size: 0.85rem;
}

.item-card-sku {
  font-size: 0.72rem;
}

.item-card-detalhes {
  font-size: 0.75rem;
}

.detalhe-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.detalhe-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.detalhe-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.detalhe-total {
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

/* MODAL */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.modal-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: calc(100% - 32px);
  max-width: 1240px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--bs-info);
  color: #fff;
  border-bottom: 1px solid var(--bs-border-color);
}

.modal-header .btn-close {
  filter: invert(1);
}

.modal-title {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
  color: #fff;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
  background: var(--bs-tertiary-bg);
}

.modal-info-box {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.info-right {
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  text-align: right;
}

.info-right .info-item {
  justify-content: flex-end;
}

.info-right .info-label {
  min-width: auto;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
  padding: 2px 0;
}

.info-item .info-label {
  font-weight: 600;
  min-width: 90px;
  color: var(--bs-secondary-color);
}

.info-item .info-valor {
  color: var(--bs-body-color);
}

/* SLIDERS */
.sliders-stacked {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.slider-section {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

.slider-section-header {
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.slider-section-header small {
  font-weight: 400;
  font-size: 0.7rem;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-row-main {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.slider-row-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.slider-row-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-row-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 110px;
}

.slider-row-value .form-control-sm {
  width: 100%;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
  text-align: center;
}

.slider-row-value .slider-max {
  font-size: 0.65rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
}

/* Botão Calcular — compacto, sem borda e com fundo de alta transparência (espelha PurchaseOrderSmartMode) */
.calcular-btn {
  margin-left: auto;
  padding: 0 8px;
  font-size: 0.68rem;
  line-height: 1.6;
  border: 0;
  border-radius: 4px;
  color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.12);
}

.calcular-btn:hover:not(:disabled) {
  background-color: rgba(var(--bs-primary-rgb), 0.22);
}

.calcular-btn:disabled {
  opacity: 0.5;
}

.slider-btn {
  font-size: 0.65rem;
  padding: 2px 8px;
  line-height: 1.3;
  border-width: 1px;
  white-space: nowrap;
  min-width: 36px;
}

.slider-btn-decimal {
  font-size: 0.6rem;
  opacity: 0.8;
  min-width: 32px;
}

/* Preço/Total/Parceiro/Lote em grid */
.campos-preco-parceiro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

/* Com parceiro habilitado, sem lote: 3 colunas (Preço, Total, Parceiro) */
.campos-preco-parceiro.com-parceiro-sem-lote {
  grid-template-columns: 1fr 1fr 1fr;
}

/* Com parceiro habilitado e lote visível: 4 colunas (Preço, Total, Parceiro, Lote) */
.campos-preco-parceiro.com-parceiro-com-lote {
  grid-template-columns: repeat(4, 1fr);
}

.campo-preco-total,
.campo-parceiro {
  display: flex;
  flex-direction: column;
}

.campo-parceiro .campo-busca-container {
  margin-bottom: 0 !important;
}

@media (max-width: 1400px) {
  .itens-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .itens-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .saldos-panel.saldos-visible {
    width: 320px;
  }
}

@media (max-width: 992px) {
  .documento-itens-smart-mode {
    flex-direction: column;
  }

  .saldos-panel.saldos-visible {
    width: 100%;
    margin-right: 0;
    margin-bottom: 16px;
  }

  .itens-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .sliders-stacked {
    grid-template-columns: 1fr;
  }

  .campos-preco-parceiro.com-parceiro-sem-lote,
  .campos-preco-parceiro.com-parceiro-com-lote {
    grid-template-columns: 1fr 1fr;
  }

  .modal-card {
    max-width: 720px;
  }
}

@media (max-width: 768px) {
  .modal-card {
    max-width: calc(100vw - 32px);
    border-radius: 8px;
  }

  .modal-info-box {
    grid-template-columns: 1fr;
  }

  .info-right {
    text-align: left;
    border-top: 1px solid var(--bs-border-color-translucent);
    padding-top: 6px;
    margin-top: 2px;
  }

  .info-right .info-item {
    justify-content: flex-start;
  }

  .campos-preco-parceiro.com-parceiro-sem-lote,
  .campos-preco-parceiro.com-parceiro-com-lote {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .itens-grid {
    grid-template-columns: 1fr;
  }

  .campos-preco-parceiro {
    grid-template-columns: 1fr;
  }
}

/* Indicador de campo obrigatório nos labels externos (fora dos componentes base com hide-label) */
.form-label.is-required::after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
}
</style>
